import { useParticipation, isParticipationDirectory } from "../../../../shared/useParticipation";
import { isFinanceDirectory, useFinanceDirectory } from "../../../../shared/useFinanceDirectory";
import { AcademicCohortFields } from "../../../../shared/AcademicCohortFields";
import { CommercialActionFields } from "../../../../shared/CommercialActionFields";
import {
  isAcademicOrganizationPortfolio,
  isAcademicCohortDirectory,
  isAcademicDirectory,
  useAcademicDirectory,
  useAcademicHistory,
  type AcademicDirectory,
} from "../../../../shared/useAcademicCatalogue";
import { TenancyActionFields } from "../../../../shared/TenancyActionFields";
import {
  useTenancyDirectory,
  useTenancyHistory,
  isTenancyDirectory,
  tenancyFormValues,
  type TenancyDirectory,
} from "../../../../shared/useTenancyDirectory";
import { IdentityAssignmentFields } from "../../../../shared/IdentityAssignmentFields";
import { IdentitySecurityControls } from "../../../../shared/IdentitySecurityControls";
import { selectIdentityWorkspace } from "../../../../shared/identityClient";
import { workspaceClients } from "../../../../shared/workspaceAccess";
import { isIdentityDirectory, useIdentityDirectory } from "../../../../shared/useIdentityDirectory";
import { useIdentity, useSignedInIdentity } from "../../../../shared/useIdentity";
import { useCoreNotifications } from "../../../../shared/useCoreNotifications";
import { useOrganizationLabAnalytics } from "../../../../shared/useOrganizationLabAnalytics";
import { ProgramAnnouncementPreference } from "../../../../shared/ProgramAnnouncementPreference";
import { platformEnvironment } from "../../config/environment";
import {
  isCommercialDirectory,
  useCommercialDirectory,
  useCommercialHistory,
  type CommercialDirectory,
} from "../../../../shared/useCommercialCatalogue";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import academyLogo from "../../../../shared/brand/chatoner-ai-academy-logo-landscape.png";
import academyMark from "../../../../shared/brand/chatoner-ai-academy-mark.png";
import { AREAS, LANGUAGES, LEGACY_ROUTE_ALIASES, ORGANIZATION_ROLES, PAGE_SPECS } from "../../data/mockData";
import type { MetricSpec, RoleProfile, RouteDef, WorkspaceRow, WorkspaceSpec } from "../../data/mockData";
import { dropdownThemeStyles, STYLES, V2_STYLES } from "../../styles/theme";
import { ICON_PATHS } from "../../config/icons";
import { readStoredJson, writeStoredJson } from "../../services/versionedStorage";
import { signOut } from "../../services/authSession";
import { downloadBrowserFile as downloadFile } from "../../utils/browser";

type Toast = { id: number; title: string; message: string; tone?: "success" | "info" | "warning" };
type ModalState = null | {
  kind: "create" | "help" | "organization" | "role" | "confirm" | "handoff" | "export";
  payload?: any;
};
type DetailState = null | { routeId: string; row: WorkspaceRow };

const API_BASE = ((globalThis as any).__CHATONER_ACADEMY_API_BASE__ || "/api/v1").replace(/\/$/, "");
const BUILD_VERSION = "2.0.0";

function Icon({ name, size = 18, className = "" }: { name: string; size?: number; className?: string }) {
  const d = ICON_PATHS[name] || ICON_PATHS.grid;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
function statusTone(value: string) {
  const v = value.toLowerCase();
  if (
    v.includes("urgent") ||
    v.includes("high") ||
    v.includes("blocked") ||
    v.includes("risk") ||
    v.includes("revoked")
  )
    return "danger";
  if (
    v.includes("due") ||
    v.includes("draft") ||
    v.includes("review") ||
    v.includes("attention") ||
    v.includes("pending") ||
    v.includes("restricted")
  )
    return "warn";
  if (
    v.includes("active") ||
    v.includes("ready") ||
    v.includes("healthy") ||
    v.includes("track") ||
    v.includes("approved") ||
    v.includes("connected") ||
    v.includes("valid")
  )
    return "good";
  return "info";
}
function Status({ children }: { children: React.ReactNode }) {
  return <span className={`status status-${statusTone(String(children))}`}>{children}</span>;
}
function WorkspaceTabPanel({
  title,
  activeTab,
  recordCount,
}: {
  title: string;
  activeTab: string;
  recordCount: number;
}) {
  const normalized = activeTab.toLowerCase();
  const focus = normalized.includes("context")
    ? "Scope, purpose and the current workspace context are in focus."
    : normalized.includes("relationship")
      ? "Linked people, permissions and responsibility boundaries are in focus."
      : normalized.includes("sponsor")
        ? "Funding, sponsor visibility and support relationships are in focus."
        : normalized.includes("transition") || normalized.includes("handoff")
          ? "Ownership changes, handoffs and the next safe action are in focus."
          : normalized.includes("timeline")
            ? "Recent activity and upcoming actions are in focus."
            : `The ${activeTab.toLowerCase()} view is in focus for this workspace.`;
  return (
    <section className="workspace-tab-panel" role="tabpanel" aria-label={`${activeTab} view`}>
      <div className="workspace-tab-panel-icon">
        <Icon name="grid" size={18} />
      </div>
      <div className="workspace-tab-panel-copy">
        <span>Current workspace view</span>
        <strong>{activeTab}</strong>
        <p>{focus}</p>
      </div>
      <div className="workspace-tab-panel-meta">
        <b>{recordCount}</b>
        <span>records in {title.toLowerCase()}</span>
      </div>
    </section>
  );
}
function Progress({ value, green = false }: { value: number; green?: boolean }) {
  const normalized = Math.max(0, Math.min(100, value));
  return (
    <div
      className={`progress ${green ? "progress-green" : ""}`}
      role="progressbar"
      aria-label={`${normalized}% complete`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={normalized}
    >
      <span style={{ width: `${normalized}%` }} />
    </div>
  );
}

function metricPercent(value: string, fallback: number) {
  const parsed = Number.parseInt(value, 10);
  return Math.max(0, Math.min(100, Number.isFinite(parsed) ? parsed : fallback));
}
function PageHero({
  kicker,
  title,
  description,
  children,
}: {
  kicker: string;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-hero">
      <div className="page-hero-copy">
        <div className="page-kicker">{kicker}</div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {children ? <div className="hero-actions">{children}</div> : null}
    </div>
  );
}
function Card({
  title,
  subtitle,
  actions,
  children,
  className = "",
}: {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {title || actions ? (
        <div className="card-header">
          <div className="card-title">
            {title ? <h3>{title}</h3> : null}
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {actions ? <div className="card-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
function Metric({
  icon,
  value,
  label,
  change,
}: {
  icon: string;
  value: string;
  label: string;
  change?: string;
}) {
  return (
    <div className="metric-card">
      <div className="metric-head">
        <div className="metric-icon">
          <Icon name={icon} size={17} />
        </div>
        {change ? <span className="metric-change">{change}</span> : null}
      </div>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
    </div>
  );
}
function Button({
  children,
  onClick,
  icon,
  variant = "",
  small = false,
  type = "button",
  disabled = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  icon?: string;
  variant?: string;
  small?: boolean;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      className={`btn ${variant ? `btn-${variant}` : ""} ${small ? "btn-sm" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon ? <Icon name={icon} size={small ? 14 : 16} /> : null}
      <span>{children}</span>
    </button>
  );
}
function safeGet<T>(key: string, fallback: T): T {
  return readStoredJson(key, fallback);
}
function safeSet(key: string, value: any) {
  writeStoredJson(key, value);
}
function csvEscape(value: any) {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function currentTime() {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date());
}
function roleHas(role: RoleProfile, key: "areas" | "manage" | "approve", area: string) {
  return role[key].includes("*") || role[key].includes(area);
}

function FilterToolbar({
  query,
  setQuery,
  status,
  setStatus,
  spec,
  onExport,
  onSavedView,
}: {
  query: string;
  setQuery: (v: string) => void;
  status: string;
  setStatus: (v: string) => void;
  spec: WorkspaceSpec;
  onExport: () => void;
  onSavedView: () => void;
}) {
  const statuses = Array.from(new Set(spec.rows.map(r => String(r.status || "Active"))));
  return (
    <div className="workspace-toolbar">
      <div className="input-wrap">
        <Icon name="search" size={14} />
        <input
          className="input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`Search ${spec.title.toLowerCase()}…`}
        />
      </div>
      <select
        className="select"
        aria-label="Filter by status"
        value={status}
        onChange={e => setStatus(e.target.value)}
      >
        <option value="All">All statuses</option>
        {statuses.map(s => (
          <option key={s}>{s}</option>
        ))}
      </select>
      <Button small icon="bookmark" onClick={onSavedView}>
        Saved view
      </Button>
      <Button small icon="download" onClick={onExport}>
        Export
      </Button>
    </div>
  );
}

function Metrics({ items }: { items: MetricSpec[] }) {
  return (
    <div className="metrics-grid">
      {items.map(item => (
        <Metric key={item.label} {...item} change={item.trend} />
      ))}
    </div>
  );
}

function RecordsTable({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const render = (row: WorkspaceRow, key: string) => {
    const value = row[key];
    if (key === "status") return <Status>{String(value)}</Status>;
    if (key === "progress")
      return (
        <div className="cell-progress">
          <Progress value={Number(value)} green />
          <small>{value}%</small>
        </div>
      );
    if (key === "evidence")
      return (
        <div className="cell-progress">
          <Progress value={parseInt(String(value))} green />
          <small>{value}</small>
        </div>
      );
    return <>{String(value ?? "—")}</>;
  };
  return (
    <div className="v2-table-wrap">
      <table className="v2-table">
        <thead>
          <tr>
            {spec.columns.map(c => (
              <th key={c.key}>{c.label}</th>
            ))}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={String(row.id || i)} onClick={() => onOpen(row)}>
              {spec.columns.map((c, index) => (
                <td key={c.key}>
                  {index === 0 ? (
                    <>
                      <b>{String(row[c.key] ?? row.name ?? "Record")}</b>
                      <span>{String(row.id || "")}</span>
                    </>
                  ) : (
                    render(row, c.key)
                  )}
                </td>
              ))}
              <td>
                <Button small icon="arrow" onClick={() => onOpen(row)}>
                  Open
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EntityCards({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-card-grid">
      {rows.map((row, i) => (
        <button
          type="button"
          className="entity-card-v2"
          key={String(row.id || i)}
          onClick={() => onOpen(row)}
        >
          <div className="entity-card-top">
            <span className="entity-card-icon">
              <Icon name={i % 2 ? spec.icon : "layers"} />
            </span>
            <span className="entity-card-copy">
              <b>{String(row.name)}</b>
              <span>
                {String(row.scope)} · {String(row.owner)}
              </span>
            </span>
            <Status>{String(row.status)}</Status>
          </div>
          <div className="entity-card-meta">
            <div>
              <span>People / units</span>
              <b>{String(row.people)}</b>
            </div>
            <div>
              <span>Progress</span>
              <b>{Number.isFinite(Number(row.progress)) ? `${row.progress}%` : "Not assessed"}</b>
            </div>
          </div>
          <div className="entity-card-foot">
            {Number.isFinite(Number(row.progress)) ? (
              <Progress value={Number(row.progress)} green />
            ) : (
              <span>Not assessed</span>
            )}
            <Icon name="arrow" size={14} />
          </div>
        </button>
      ))}
    </div>
  );
}

function MiniChart({ values }: { values: number[] }) {
  return (
    <div className="chart-shell">
      <div className="chart-columns-v2">
        {values.map((value, i) => (
          <div className="chart-column-v2" key={i} style={{ height: `${value}%` }}>
            <span>{value}%</span>
            <i>{["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8"][i]}</i>
          </div>
        ))}
      </div>
    </div>
  );
}

function Notes({ spec }: { spec: WorkspaceSpec }) {
  return (
    <div className="policy-list">
      {spec.notes.map((note, i) => (
        <div className="policy-item" key={note.title}>
          <Icon name={i === 0 ? "shield" : i === 1 ? "link" : "database"} size={15} />
          <span>
            <strong>{note.title}</strong>
            <span>{note.body}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function WorkspaceHero({
  spec,
  canManage,
  onPrimary,
  onExport,
}: {
  spec: WorkspaceSpec;
  canManage: boolean;
  onPrimary: () => void;
  onExport: () => void;
}) {
  return (
    <PageHero kicker={`${spec.areaLabel} · ${spec.group}`} title={spec.title} description={spec.description}>
      <Button icon="plus" variant="primary" onClick={onPrimary} disabled={!canManage}>
        {spec.primaryAction}
      </Button>
      <Button icon="download" onClick={onExport}>
        Export
      </Button>
    </PageHero>
  );
}

function AccessBoundary({
  spec,
  role,
  onSwitchRole,
}: {
  spec: WorkspaceSpec;
  role: RoleProfile;
  onSwitchRole: () => void;
}) {
  return (
    <div className="access-boundary">
      <span className="big-icon">
        <Icon name="lock" size={28} />
      </span>
      <h2>This role does not have access to {spec.title}.</h2>
      <p>
        {role.name} is scoped to {role.scope}. Access is enforced by role, delegated organization scope,
        purpose, data sensitivity, approval state and expiry—not merely by hiding navigation.
      </p>
      <div className="hero-actions">
        <Button icon="users" variant="primary" onClick={onSwitchRole}>
          Preview another organization role
        </Button>
        <Button icon="help" onClick={() => {}}>
          Request governed access
        </Button>
      </div>
    </div>
  );
}

function DashboardLayout({
  spec,
  rows,
  onOpen,
  onNavigate,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onNavigate: (area: string, page: string) => void;
}) {
  return (
    <>
      <div className="v2-grid">
        <div className="v2-stack">
          <Card
            title="Organizational momentum"
            subtitle="Eight-week composite of learning delivery, workforce capability, responsible adoption, capacity and connected-platform activity."
          >
            <MiniChart values={spec.chart} />
          </Card>
          <Card title="Priority work" subtitle="Named owners and governed next actions.">
            <div className="list">
              {rows.map(row => (
                <button
                  type="button"
                  className="list-row"
                  key={String(row.id)}
                  onClick={() => onOpen(row)}
                  style={{ width: "100%", textAlign: "left", color: "inherit" }}
                >
                  <span className="list-icon">
                    <Icon name={spec.icon} />
                  </span>
                  <span className="list-copy">
                    <b>{String(row.name)}</b>
                    <span>
                      {String(row.owner)} · {String(row.scope)} · updated {String(row.updated)}
                    </span>
                  </span>
                  <Status>{String(row.status)}</Status>
                  <Icon name="arrow" size={14} />
                </button>
              ))}
            </div>
          </Card>
        </div>
        <div className="v2-stack">
          <Card title="Organization health domains" subtitle="Open a specialist workspace for action.">
            <div className="health-domain-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {spec.metrics.map((m, i) => (
                <button
                  type="button"
                  className="health-domain"
                  key={m.label}
                  onClick={() =>
                    onNavigate(
                      ["reports", "learning", "governance", "finance"][i % 4],
                      ["reports-executive", "learning-programs", "governance-overview", "finance-overview"][
                        i % 4
                      ],
                    )
                  }
                >
                  <span>{m.label}</span>
                  <strong>{m.value}</strong>
                  <small>{m.trend}</small>
                </button>
              ))}
            </div>
          </Card>
          <Card title="Governance notes">
            <Notes spec={spec} />
          </Card>
        </div>
      </div>
    </>
  );
}

function QueueLayout({
  spec: _spec,
  rows,
  onOpen,
  onMove,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onMove: (id: string, status: string) => void;
}) {
  const groups = ["Urgent", "Review due", "On track", "Ready"];
  return (
    <div className="kanban-v2">
      {groups.map(group => {
        const items = rows.filter(
          (r, i) => String(r.status) === group || i % groups.length === groups.indexOf(group),
        );
        return (
          <section className="kanban-column" key={group}>
            <div className="kanban-head">
              <b>{group}</b>
              <span>{items.length}</span>
            </div>
            {items.map(row => (
              <article className="kanban-card" key={String(row.id)}>
                <b>{String(row.name)}</b>
                <p>
                  {String(row.owner)} · {String(row.scope)}
                </p>
                <Status>{String(row.status)}</Status>
                <div className="kanban-card-actions">
                  <button type="button" onClick={() => onOpen(row)}>
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(String(row.id), group === "Ready" ? "Active" : "Ready")}
                  >
                    {group === "Ready" ? "Reopen" : "Advance"}
                  </button>
                </div>
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ScorecardLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <Card
          title={`${spec.title} trend`}
          subtitle="Privacy-thresholded, explainable organizational measure."
        >
          <MiniChart values={spec.chart} />
        </Card>
        <Card
          title="Domain drill-down"
          subtitle="Select a row to inspect measure definition, owner, source and actions."
        >
          <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        </Card>
      </div>
      <div className="v2-stack">
        <Card title="Current domains">
          <div className="health-domain-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="health-domain" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <small>{m.trend}</small>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Measure governance">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function CalendarLayout({
  spec: _spec,
  rows,
  onOpen,
  onAdd,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onAdd: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const base = new Date(2026, 7 + offset, 1);
  const title = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(base);
  const first = base.getDay();
  const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const prev = new Date(base.getFullYear(), base.getMonth(), 0).getDate();
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = i - first + 1;
    return d < 1
      ? { day: prev + d, muted: true }
      : d > days
        ? { day: d - days, muted: true }
        : { day: d, muted: false };
  });
  return (
    <div className="calendar-v2">
      <section>
        <div className="month-toolbar">
          <Button small icon="arrow" onClick={() => setOffset(v => v - 1)}>
            Previous
          </Button>
          <strong>{title}</strong>
          <Button small onClick={() => setOffset(0)}>
            Today
          </Button>
          <Button small icon="arrow" onClick={() => setOffset(v => v + 1)}>
            Next
          </Button>
          <Button small icon="plus" variant="primary" onClick={onAdd}>
            Add
          </Button>
        </div>
        <div className="month-grid-v2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <div className="month-cell head" key={d}>
              {d}
            </div>
          ))}
          {cells.map((cell, i) => (
            <div
              className={`month-cell ${cell.muted ? "muted" : ""} ${!cell.muted && cell.day === 25 ? "today" : ""}`}
              key={`${cell.day}-${i}`}
            >
              <span className="month-day">{cell.day}</span>
              {!cell.muted
                ? rows
                    .filter((_, idx) => (idx * 5 + 3) % days === cell.day % days)
                    .slice(0, 2)
                    .map(row => (
                      <button
                        type="button"
                        className="month-event"
                        key={String(row.id)}
                        onClick={() => onOpen(row)}
                      >
                        {String(row.name)}
                      </button>
                    ))
                : null}
            </div>
          ))}
        </div>
      </section>
      <Card title="Upcoming organizational items" subtitle={`${rows.length} connected items`}>
        <div className="timeline">
          {rows.map(row => (
            <button
              type="button"
              className="timeline-item"
              key={String(row.id)}
              onClick={() => onOpen(row)}
              style={{
                width: "100%",
                textAlign: "left",
                color: "inherit",
                background: "transparent",
                border: 0,
              }}
            >
              <b>{String(row.name)}</b>
              <p>
                {String(row.date)} · {String(row.time)} · {String(row.owner)}
              </p>
              <time>{String(row.status)}</time>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FormLayout({
  spec,
  values,
  setValues,
  onSave,
  onHistory,
  canManage,
}: {
  spec: WorkspaceSpec;
  values: Record<string, string>;
  setValues: (v: Record<string, string>) => void;
  onSave: () => void;
  onHistory?: () => void;
  canManage: boolean;
}) {
  const liveTenancy = spec.id === "settings-profile" || spec.id === "institution-profile";
  const mid = Math.ceil(spec.fields.length / 2);
  const groups = [spec.fields.slice(0, mid), spec.fields.slice(mid)];
  return (
    <div className="form-layout-v2">
      <div className="form-sections">
        {groups.map((fields, i) => (
          <section className="form-section-v2" key={i}>
            <h3>{i === 0 ? "Primary configuration" : "Governance and defaults"}</h3>
            <div className="form-fields-v2">
              {fields.map(field => (
                <div
                  className={`form-field-v2 ${field.toLowerCase().includes("notes") || field.toLowerCase().includes("policy") ? "full" : ""}`}
                  key={field}
                >
                  <label>{field}</label>
                  {field.toLowerCase().includes("notes") || field.toLowerCase().includes("policy") ? (
                    <textarea
                      value={values[field] || ""}
                      onChange={e => setValues({ ...values, [field]: e.target.value })}
                      disabled={
                        !canManage ||
                        (spec.id === "institution-profile" && field === "Accreditation reference")
                      }
                    />
                  ) : field.toLowerCase().includes("language") || field.toLowerCase().includes("status") ? (
                    <select
                      value={
                        spec.id === "settings-profile" || spec.id === "institution-profile"
                          ? values[field] || ""
                          : values[field] || "Active"
                      }
                      onChange={e => setValues({ ...values, [field]: e.target.value })}
                      disabled={
                        !canManage ||
                        (spec.id === "institution-profile" && field === "Accreditation reference")
                      }
                    >
                      <option value="">Choose a value</option>
                      {spec.id !== "settings-profile" && spec.id !== "institution-profile" && (
                        <option>Active</option>
                      )}
                      <option>English</option>
                      <option>Luganda</option>
                      <option>Kiswahili</option>
                      {spec.id !== "settings-profile" && spec.id !== "institution-profile" && (
                        <>
                          <option>Draft</option>
                          <option>Review required</option>
                        </>
                      )}
                    </select>
                  ) : (
                    <input
                      placeholder={
                        field === "Country and time zone"
                          ? "Country | IANA time zone"
                          : field === "Accreditation reference"
                            ? "Managed through accreditation"
                            : undefined
                      }
                      value={
                        spec.id === "settings-profile" || spec.id === "institution-profile"
                          ? values[field] || ""
                          : values[field] || `${field} value`
                      }
                      onChange={e => setValues({ ...values, [field]: e.target.value })}
                      disabled={
                        !canManage ||
                        (spec.id === "institution-profile" && field === "Accreditation reference")
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
        <div className="hero-actions" style={{ justifyContent: "flex-end" }}>
          <Button icon="history" onClick={onHistory} disabled={liveTenancy && !onHistory}>
            View change history
          </Button>
          <Button icon="check" variant="primary" onClick={onSave} disabled={!canManage}>
            Save governed changes
          </Button>
        </div>
      </div>
      <div className="v2-stack">
        <Card title="Configuration readiness">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="capacity-card">
              <span>Completeness</span>
              <strong>{liveTenancy ? "Not assessed" : "92%"}</strong>
              <div className="capacity-bar">
                <i style={{ width: liveTenancy ? "0%" : "92%" }} />
              </div>
            </div>
            <div className="capacity-card">
              <span>Approval state</span>
              <strong>{liveTenancy ? "Not assessed" : canManage ? "Ready" : "Read-only"}</strong>
              <div className="capacity-bar">
                <i style={{ width: liveTenancy ? "0%" : canManage ? "78%" : "40%" }} />
              </div>
            </div>
          </div>
        </Card>
        <Card title="Change controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function HierarchyLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const liveTenancy =
    rows.some(row => "parentId" in row) ||
    spec.id === "people-teams" ||
    ["campuses", "schools-faculties", "departments", "academic-units"].includes(spec.id);
  const levels: WorkspaceRow[][] = [];
  if (liveTenancy) {
    const byId = new Map(rows.map(row => [String(row.id), row]));
    for (const row of rows) {
      let parent = String(row.parentId || ""),
        depth = 0;
      const visited = new Set([String(row.id)]);
      while (parent && byId.has(parent) && !visited.has(parent) && depth < 16) {
        visited.add(parent);
        depth++;
        parent = String(byId.get(parent)?.parentId || "");
      }
      (levels[depth] ||= []).push(row);
    }
  }
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        {liveTenancy ? (
          <div className="hierarchy-canvas">
            <div className="hierarchy-level">
              <button type="button" className="hierarchy-node root" disabled>
                <b>{String(rows[0]?.entityName || "No structure records")}</b>
                <span>Organization structure</span>
              </button>
            </div>
            {levels.map((level, index) => (
              <div key={index}>
                <div className="hierarchy-connector" />
                <div className="hierarchy-level">
                  {level.map(row => (
                    <button
                      type="button"
                      className="hierarchy-node"
                      key={String(row.id)}
                      onClick={() => onOpen(row)}
                    >
                      <b>{String(row.name)}</b>
                      <span>
                        {String(row.scope)} · {String(row.people)} people
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="hierarchy-canvas">
            <div className="hierarchy-level">
              <button type="button" className="hierarchy-node root" onClick={() => onOpen(rows[0])}>
                <b>{spec.title}</b>
                <span>Organization root · delegated authority</span>
              </button>
            </div>
            <div className="hierarchy-connector" />
            <div className="hierarchy-level">
              {rows.slice(0, 3).map(row => (
                <button
                  type="button"
                  className="hierarchy-node"
                  key={String(row.id)}
                  onClick={() => onOpen(row)}
                >
                  <b>{String(row.name)}</b>
                  <span>
                    {String(row.owner)} · {String(row.people)} people
                  </span>
                </button>
              ))}
            </div>
            <div className="hierarchy-connector" />
            <div className="hierarchy-level">
              {rows.slice(3).map(row => (
                <button
                  type="button"
                  className="hierarchy-node"
                  key={String(row.id)}
                  onClick={() => onOpen(row)}
                >
                  <b>{String(row.name)}</b>
                  <span>{String(row.scope)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <div className="v2-stack">
        <Card title="Delegated administration">
          <Notes spec={spec} />
        </Card>
        <Card title="Structure readiness">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map((m, index) => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: liveTenancy ? "0%" : `${68 + ((index * 7) % 27)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function DirectoryLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div>
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <div className="v2-stack">
        <Card title="Role and status summary">
          <div className="health-domain-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="health-domain" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <small>{m.trend}</small>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Access and privacy">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function OnboardingLayout({
  spec,
  rows,
  onOpen,
  onAdvance,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onAdvance: (id: string) => void;
}) {
  const nativeCohorts = spec.id === "learning-cohorts";
  const stages = nativeCohorts
    ? ["Planning", "Open / Limited / Full", "Paused", "Closed", "Completed / Cancelled"]
    : ["Invited", "Identity", "Role setup", "Workspace ready", "Activated"];
  const groups = [
    ["planning"],
    ["open", "limited", "full"],
    ["paused"],
    ["closed"],
    ["completed", "cancelled"],
  ];
  const inStage = (row: WorkspaceRow, index: number, stage: number) =>
    nativeCohorts ? groups[stage].includes(String(row.status)) : index % stages.length === stage;
  return (
    <>
      <div className="onboarding-pipeline">
        {stages.map((stage, si) => (
          <section className="pipeline-column" key={stage}>
            <div className="pipeline-head">
              <b>{stage}</b>
              <span>{rows.filter((row, i) => inStage(row, i, si)).length}</span>
            </div>
            {rows
              .filter((row, i) => inStage(row, i, si))
              .map(row => (
                <article className="pipeline-card" key={String(row.id)} onClick={() => onOpen(row)}>
                  <b>{String(row.name)}</b>
                  <span>
                    {String(row.owner)} · {String(row.scope)}
                  </span>
                  {Number.isFinite(Number(row.progress)) ? (
                    <Progress value={Number(row.progress)} green />
                  ) : (
                    <span>Not assessed</span>
                  )}
                  <div style={{ display: "flex", gap: 5, marginTop: 7 }}>
                    <Status>{String(row.status)}</Status>
                    {si < stages.length - 1 ? (
                      <button
                        type="button"
                        disabled={nativeCohorts && ["completed", "cancelled"].includes(String(row.status))}
                        className="btn btn-sm"
                        onClick={e => {
                          e.stopPropagation();
                          onAdvance(String(row.id));
                        }}
                      >
                        Advance
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
          </section>
        ))}
      </div>
      <Card
        title={nativeCohorts ? "Cohort policy" : "Onboarding policy"}
        subtitle={
          nativeCohorts
            ? "Cohort definitions preserve their exact academic versions. Membership and enrollment are separate."
            : "Roles are activated only after identity, authority, scope and required acknowledgements are verified."
        }
      >
        <Notes spec={spec} />
      </Card>
    </>
  );
}

function MatrixLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const headers = ["View", "Create", "Edit", "Approve", "Export", "Emergency"];
  return (
    <div className="v2-grid">
      <div className="matrix-wrap">
        <table className="matrix-table">
          <thead>
            <tr>
              <th>{spec.columns[0]?.label || "Item"}</th>
              {headers.map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={String(row.id)} onClick={() => onOpen(row)}>
                <td>
                  <b>{String(row.name)}</b>
                  <div style={{ color: "var(--muted)", fontSize: 14, marginTop: 3 }}>{String(row.scope)}</div>
                </td>
                {headers.map((h, ci) => {
                  if (Object.prototype.hasOwnProperty.call(row, "permissions"))
                    return (
                      <td key={h} title="Open the role for its exact assigned permissions">
                        —
                      </td>
                    );
                  const kind = (ri + ci) % 5;
                  return (
                    <td className="matrix-check" key={h}>
                      <span className={`matrix-dot ${kind === 3 ? "gap" : kind === 4 ? "none" : ""}`}>
                        <Icon name={kind === 3 ? "alert" : kind === 4 ? "close" : "check"} size={12} />
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="v2-stack">
        <Card title="Coverage summary">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: `${metricPercent(m.value, 76)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Matrix controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function ImportLayout({
  spec,
  rows,
  onOpen,
  onRun,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onRun: () => void;
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState("");
  return (
    <div className="v2-stack">
      <div className="import-stepper">
        {["Select source", "Map fields", "Validate", "Approve & import"].map((label, i) => (
          <button
            type="button"
            disabled={i + 1 > step}
            className={`import-step ${step === i + 1 ? "active" : ""} ${step > i + 1 ? "done" : ""}`}
            key={label}
            onClick={() => setStep(i + 1)}
          >
            <span>Step {i + 1}</span>
            <b>{label}</b>
          </button>
        ))}
      </div>
      <div className="v2-grid equal">
        <label className="drop-zone">
          <input
            type="file"
            accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: "none" }}
            onChange={e => {
              setFile(e.target.files?.[0]?.name || "");
              setStep(1);
            }}
          />
          <span className="drop-icon">
            <Icon name="upload" size={22} />
          </span>
          <b>{file || "Choose an organization data file"}</b>
          <span>CSV, XLSX or signed API manifest · validation occurs before any record is changed.</span>
        </label>
        <Card title="Current validation" subtitle={`Step ${step} of 4`}>
          <div className="policy-list">
            <div className="policy-item">
              <Icon name="check" />
              <span>
                <strong>Schema detected</strong>
                <span>Organization identifiers and required fields mapped</span>
              </span>
            </div>
            <div className="policy-item">
              <Icon name="alert" />
              <span>
                <strong>3 potential duplicates</strong>
                <span>Existing identities require human review</span>
              </span>
            </div>
            <div className="policy-item">
              <Icon name="shield" />
              <span>
                <strong>No destructive overwrite</strong>
                <span>Production imports are idempotent and audited</span>
              </span>
            </div>
          </div>
          <div className="hero-actions">
            <Button
              icon="arrow"
              variant="primary"
              disabled={!file}
              onClick={() => {
                setStep(v => Math.min(4, v + 1));
                if (step === 4) onRun();
              }}
            >
              {step === 4 ? "Run governed import" : "Continue"}
            </Button>
            {!file ? <span className="import-requirement">Choose a data file to continue.</span> : null}
          </div>
        </Card>
      </div>
      <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
    </div>
  );
}

function PortfolioLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      <div className="v2-grid equal">
        <Card title="Portfolio progress">
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Governance and next actions">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function MapLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="path-canvas">
        {rows.map((row, i) => (
          <React.Fragment key={String(row.id)}>
            <button
              type="button"
              className={`path-node ${i === 1 ? "active" : ""}`}
              onClick={() => onOpen(row)}
            >
              <b>{String(row.name)}</b>
              <span>
                {String(row.scope)} · {String(row.evidence)} coverage
              </span>
            </button>
            {i < rows.length - 1 ? <Icon name="arrow" className="path-arrow" /> : null}
          </React.Fragment>
        ))}
      </div>
      <div className="v2-grid equal">
        <Card title="Mapping and decision table">
          <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        </Card>
        <Card title="Route rules">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function TableLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
      <div className="v2-stack">
        <Card title="Record readiness">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: `${metricPercent(m.value, 72)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Official controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function TimetableLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const times = ["08:00", "10:00", "12:00", "14:00", "16:00"];
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  return (
    <div className="v2-stack">
      <div className="timetable-shell">
        <div className="timetable-grid">
          <div className="time-cell head">Time</div>
          {days.map(d => (
            <div className="time-cell head" key={d}>
              {d}
            </div>
          ))}
          {times.map((time, ti) => (
            <React.Fragment key={time}>
              <div className="time-cell label">{time}</div>
              {days.map((day, di) => {
                const row = rows[(ti + di) % rows.length];
                return (
                  <div className="time-cell" key={day}>
                    <button
                      type="button"
                      className={`schedule-block ${(ti + di) % 2 ? "alt" : ""}`}
                      onClick={() => onOpen(row)}
                    >
                      <b>{String(row.name)}</b>
                      <span>
                        {String(row.owner)} · {String(row.scope)}
                      </span>
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="v2-grid equal">
        <Card title="Conflict and capacity checks">
          <Notes spec={spec} />
        </Card>
        <Card title="Scheduled items">
          <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        </Card>
      </div>
    </div>
  );
}

function LibraryLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid four">
        {rows.map((row, i) => (
          <button type="button" className="entity-card-v2" key={String(row.id)} onClick={() => onOpen(row)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name={["book", "file", "video", "database"][i % 4]} />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.category)} · {String(row.scope)}
                </span>
              </span>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Accessibility</span>
                <b>{String(row.evidence)}</b>
              </div>
              <div>
                <span>Owner</span>
                <b>{String(row.owner)}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Status>{String(row.status)}</Status>
              <span className="toolbar-spacer" />
              <Icon name="arrow" size={14} />
            </div>
          </button>
        ))}
      </div>
      <Card title="Resource governance">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function ConnectionLayout({
  spec,
  rows,
  onOpen,
  onTest,
  onHandoff,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onTest: () => void;
  onHandoff: () => void;
}) {
  return (
    <div className="v2-stack">
      <section className="connector-hero">
        <div className="connector-copy">
          <div className="page-kicker">Independent service connection</div>
          <h3>{spec.title}</h3>
          <p>{spec.description}</p>
          <span className="connector-domain">
            <Icon name="external" size={13} />
            {spec.external?.domain || "Organization-approved connector"} ·{" "}
            {spec.external?.mode || "governed integration"}
          </span>
          <div className="hero-actions">
            <Button icon="pulse" variant="primary" onClick={onTest}>
              Test connection
            </Button>
            <Button icon="external" variant="green" onClick={onHandoff}>
              Open authorized workspace
            </Button>
          </div>
        </div>
        <div className="connector-status">
          <div>
            <span>Connection status</span>
            <b>Connected · 99.98%</b>
          </div>
          <div>
            <span>Last signed handoff</span>
            <b>8 minutes ago</b>
          </div>
          <div>
            <span>Data scope</span>
            <b>Minimum necessary</b>
          </div>
          <div>
            <span>Named owner</span>
            <b>Learning Operations / IT</b>
          </div>
        </div>
      </section>
      <div className="handoff-flow">
        {[
          ["01", "Organization", "Identity, role and entitlement"],
          ["02", "Signed handoff", "Audience-bound one-time token"],
          ["03", "Independent service", "Own authorization and policy"],
          ["04", "Safe return", "Approved summary or evidence"],
        ].map(item => (
          <div className="handoff-step" key={item[0]}>
            <span>{item[0]}</span>
            <b>{item[1]}</b>
            <p>{item[2]}</p>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <Card title="Trust boundaries">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function SessionsLayout({
  spec,
  rows,
  onOpen,
  onHandoff,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onHandoff: () => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid two">
        {rows.map(row => (
          <article className="entity-card-v2" key={String(row.id)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name="video" />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.date)} · {String(row.time)}
                </span>
              </span>
              <Status>{String(row.status)}</Status>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Expected</span>
                <b>{String(row.people)}</b>
              </div>
              <div>
                <span>Instructor / owner</span>
                <b>{String(row.owner)}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Prepare
              </Button>
              <Button small icon="external" variant="green" onClick={onHandoff}>
                Enter Class
              </Button>
            </div>
          </article>
        ))}
      </div>
      <Card title="Live-class readiness">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function PolicyLayout({
  spec,
  rows,
  toggles,
  setToggles,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  toggles: Record<string, boolean>;
  setToggles: (v: Record<string, boolean>) => void;
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      {spec.id === "settings-security" && (
        <div style={{ gridColumn: "1 / -1" }}>
          <IdentitySecurityControls />
        </div>
      )}
      <div className="policy-grid">
        {rows.map((row, i) => (
          <article className="policy-card-v2" key={String(row.id)}>
            <div className="policy-title">
              <span>
                <Icon name={i % 2 ? "shield" : "file"} />
              </span>
              <b>{String(row.name)}</b>
              <span className="toolbar-spacer" />
              <Status>{String(row.status)}</Status>
            </div>
            <p>
              {String(row.scope)} · owner {String(row.owner)} · next review {String(row.date)}
            </p>
            <div className="toggle-row-v2">
              <span>Policy enabled and enforced</span>
              <button
                type="button"
                className={`toggle-v2 ${toggles[String(row.id)] !== false ? "on" : ""}`}
                onClick={() =>
                  setToggles({ ...toggles, [String(row.id)]: toggles[String(row.id)] === false })
                }
              />
            </div>
            <div className="entity-card-foot">
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Open version
              </Button>
              <Button small icon="history">
                History
              </Button>
            </div>
          </article>
        ))}
      </div>
      <div className="v2-stack">
        <Card title="Control posture">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: `${metricPercent(m.value, 78)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Policy authority">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function CapacityLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="capacity-grid">
        {spec.metrics.map((m, i) => (
          <div className="capacity-card" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
            <div className="capacity-bar">
              <i style={{ width: `${60 + i * 9}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <Card title="Capacity controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function EvidenceLayout({
  spec,
  rows,
  onOpen,
  onDecision,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onDecision: (id: string, status: string) => void;
}) {
  const lanes = ["Pending", "Verified", "Needs information"];
  return (
    <div className="v2-stack">
      <div className="evidence-lanes">
        {lanes.map((lane, li) => (
          <section className="evidence-lane" key={lane}>
            <h4>
              <Icon name={li === 1 ? "check" : "award"} size={14} />
              {lane}
            </h4>
            {rows
              .filter((_, i) => i % lanes.length === li)
              .map(row => (
                <article className="evidence-item-v2" key={String(row.id)} onClick={() => onOpen(row)}>
                  <b>{String(row.name)}</b>
                  <span>
                    {String(row.owner)} · {String(row.evidence)} complete
                  </span>
                  <div className="entity-card-foot">
                    <Status>{String(row.status)}</Status>
                    {li === 0 ? (
                      <Button small icon="check" onClick={() => onDecision(String(row.id), "Verified")}>
                        Verify
                      </Button>
                    ) : null}
                  </div>
                </article>
              ))}
          </section>
        ))}
      </div>
      <div className="v2-grid equal">
        <Card title="Evidence coverage">
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Decision authority">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function AnalyticsLayout({ spec, onGenerate }: { spec: WorkspaceSpec; onGenerate: () => void }) {
  const [measures, setMeasures] = useState<string[]>([spec.metrics[0].label, spec.metrics[1].label]);
  const [threshold, setThreshold] = useState("10");
  const toggle = (m: string) => setMeasures(v => (v.includes(m) ? v.filter(x => x !== m) : [...v, m]));
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <Card
          title="Report definition"
          subtitle="Select explainable measures, audience and privacy controls."
        >
          <div className="form-fields-v2">
            {spec.metrics.map(m => (
              <label className="policy-card-v2" key={m.label} style={{ cursor: "pointer" }}>
                <div className="policy-title">
                  <span>
                    <Icon name={m.icon} />
                  </span>
                  <b>{m.label}</b>
                  <input
                    type="checkbox"
                    checked={measures.includes(m.label)}
                    onChange={() => toggle(m.label)}
                    style={{ marginLeft: "auto" }}
                  />
                </div>
                <p>{m.trend} · source ownership and definitions will be attached.</p>
              </label>
            ))}
            <div className="form-field-v2">
              <label>Minimum reportable group</label>
              <select value={threshold} onChange={e => setThreshold(e.target.value)}>
                <option>5</option>
                <option>10</option>
                <option>15</option>
                <option>20</option>
              </select>
            </div>
            <div className="form-field-v2">
              <label>Audience</label>
              <select>
                <option>University Senate</option>
                <option>Dean and Heads</option>
                <option>Governance Office</option>
                <option>Registrar</option>
              </select>
            </div>
          </div>
          <div className="hero-actions">
            <Button icon="chart" variant="primary" onClick={onGenerate}>
              Generate governed preview
            </Button>
          </div>
        </Card>
      </div>
      <div className="v2-stack">
        <Card title="Live preview" subtitle={`${measures.length} measures · minimum group ${threshold}`}>
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Export controls">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function BoardLayout({
  spec: _spec,
  rows,
  onOpen,
  onMove,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onMove: (id: string, status: string) => void;
}) {
  const stages = ["Draft", "Active", "Review due", "Ready"];
  return (
    <div className="kanban-v2">
      {stages.map((stage, si) => (
        <section className="kanban-column" key={stage}>
          <div className="kanban-head">
            <b>{stage}</b>
            <span>{rows.filter((_, i) => i % stages.length === si).length}</span>
          </div>
          {rows
            .filter((_, i) => i % stages.length === si)
            .map(row => (
              <article className="kanban-card" key={String(row.id)}>
                <b>{String(row.name)}</b>
                <p>
                  {String(row.owner)} · {String(row.scope)}
                </p>
                {Number.isFinite(Number(row.progress)) ? (
                  <Progress value={Number(row.progress)} green />
                ) : (
                  <span>Not assessed</span>
                )}
                <div className="kanban-card-actions">
                  <button type="button" onClick={() => onOpen(row)}>
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => onMove(String(row.id), stage === "Ready" ? "Active" : "Ready")}
                  >
                    {stage === "Ready" ? "Reopen" : "Advance"}
                  </button>
                </div>
              </article>
            ))}
        </section>
      ))}
    </div>
  );
}

function RubricLayout({
  spec,
  rows,
  onSave,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onSave: () => void;
}) {
  const levels = ["Exemplary", "Proficient", "Developing", "Insufficient"];
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <div className="v2-stack">
      <div className="workspace-toolbar">
        <span className="view-chip">
          <Icon name="target" size={13} />
          <strong>{rows.length}</strong> criteria
        </span>
        <span className="view-chip">
          <Icon name="link" size={13} />
          Outcome aligned
        </span>
        <span className="toolbar-spacer" />
        <Button icon="check" variant="primary" onClick={onSave} disabled>
          Save rubric version
        </Button>
      </div>
      <p role="status">Preview · Rubric authoring requires the governed D11 assessment workflow.</p>
      <div className="rubric-editor">
        <div className="rubric-row">
          <div className="rubric-cell head">Criterion</div>
          {levels.map(l => (
            <div className="rubric-cell head" key={l}>
              {l}
            </div>
          ))}
        </div>
        {rows.map(row => (
          <div className="rubric-row" key={String(row.id)}>
            <div className="rubric-cell">
              <b>{String(row.name)}</b>
              <div style={{ color: "var(--muted)", fontSize: 14, marginTop: 5 }}>{String(row.scope)}</div>
            </div>
            {levels.map(level => (
              <div className="rubric-cell" key={level}>
                <textarea
                  value={
                    values[`${row.id}-${level}`] ||
                    `${level} evidence descriptor for ${String(row.name).toLowerCase()}.`
                  }
                  onChange={e => setValues({ ...values, [`${row.id}-${level}`]: e.target.value })}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <Card title="Calibration and version control">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function MediaLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="media-grid-v2">
        {rows.map(row => (
          <article className="media-card-v2" key={String(row.id)}>
            <div className="media-preview">
              <button type="button" className="media-play" onClick={() => onOpen(row)}>
                <Icon name="play" size={20} />
              </button>
            </div>
            <div className="media-card-body">
              <b>{String(row.name)}</b>
              <p>
                {String(row.date)} · {String(row.scope)} · {String(row.evidence)} captions
              </p>
              <div className="media-card-actions">
                <Button small icon="play" onClick={() => onOpen(row)}>
                  Watch
                </Button>
                <Button small icon="file">
                  Transcript
                </Button>
                <Button small icon="download">
                  Download
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>
      <Card title="Recording, caption and retention controls">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function WellbeingLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="protected-callout">
        <span className="icon-wrap">
          <Icon name="shield" size={18} />
        </span>
        <div>
          <b>Protected support workspace</b>
          <p>
            Case visibility is purpose-limited and minimum-necessary. Aggregate analytics, sponsors,
            instructors and technical administrators do not automatically receive protected case information.
          </p>
        </div>
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <div className="v2-stack">
          <Card title="Urgent route">
            <div className="policy-list">
              <div className="policy-item">
                <Icon name="alert" />
                <span>
                  <strong>Immediate danger</strong>
                  <span>Use the organization emergency and safeguarding procedure now.</span>
                </span>
              </div>
              <div className="policy-item">
                <Icon name="users" />
                <span>
                  <strong>Named safeguarding owner</strong>
                  <span>Only authorized responders can open full case information.</span>
                </span>
              </div>
              <div className="policy-item">
                <Icon name="history" />
                <span>
                  <strong>Attributable timeline</strong>
                  <span>Access and decisions are retained for review.</span>
                </span>
              </div>
            </div>
          </Card>
          <Card title="Case controls">
            <Notes spec={spec} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function AdvisingLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <div className="v2-stack">
        <Card title="Advisor caseload">
          <div className="capacity-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {spec.metrics.map(m => (
              <div className="capacity-card" key={m.label}>
                <span>{m.label}</span>
                <strong>{m.value}</strong>
                <div className="capacity-bar">
                  <i style={{ width: `${metricPercent(m.value, 70)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Documented next actions">
          <div className="timeline">
            {rows.map(row => (
              <div className="timeline-item" key={String(row.id)}>
                <b>{String(row.name)}</b>
                <p>
                  {String(row.owner)} · next meeting {String(row.date)}
                </p>
                <time>{String(row.status)}</time>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function AidLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="finance-summary">
        {spec.metrics.map(m => (
          <div className="finance-card" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
            <small>{m.trend}</small>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
        <Card title="Protected finance boundaries">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function PartnershipLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <EntityCards spec={spec} rows={rows} onOpen={onOpen} />
      <Card title="Partnership milestone map">
        <div className="path-canvas">
          {rows.map((row, i) => (
            <React.Fragment key={String(row.id)}>
              <button
                type="button"
                className={`path-node ${i === 0 ? "active" : ""}`}
                onClick={() => onOpen(row)}
              >
                <b>{String(row.name)}</b>
                <span>
                  {String(row.people)} learners · {String(row.date)}
                </span>
              </button>
              {i < rows.length - 1 ? <Icon name="arrow" className="path-arrow" /> : null}
            </React.Fragment>
          ))}
        </div>
      </Card>
    </div>
  );
}

function PublicationsLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid">
        {rows.map((row, i) => (
          <article className="entity-card-v2" key={String(row.id)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name={i % 2 ? "file" : "book"} />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.category)} · {String(row.owner)}
                </span>
              </span>
              <Status>{String(row.status)}</Status>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Repository</span>
                <b>{String(row.decision)}</b>
              </div>
              <div>
                <span>Updated</span>
                <b>{String(row.updated)}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Inspect
              </Button>
              <Button small icon="external">
                Repository
              </Button>
            </div>
          </article>
        ))}
      </div>
      <Card title="Publication and repository controls">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function VerificationLayout({
  spec: _spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  const [code, setCode] = useState(String(rows[0]?.name || ""));
  const [result, setResult] = useState<WorkspaceRow | null>(null);
  const verify = () =>
    setResult(rows.find(r => String(r.name).toLowerCase() === code.trim().toLowerCase()) || rows[0]);
  return (
    <div className="verification-shell">
      <section className="verification-box">
        <div className="page-kicker">Public verification boundary</div>
        <h3>Verify an organization or Academy credential</h3>
        <p>
          Verification confirms issuer, credential, status and permitted public information without exposing
          private learner evidence.
        </p>
        <div className="verify-input">
          <input
            value={code}
            onChange={e => {
              setCode(e.target.value);
              setResult(null);
            }}
            placeholder="Enter verification ID"
          />
          <Button icon="search" variant="primary" onClick={verify}>
            Verify
          </Button>
        </div>
        {result ? (
          <div className="verification-result">
            <b>Valid verification record</b>
            <p>
              {String(result.name)} · {String(result.owner)} · status {String(result.status)} · evidence{" "}
              {String(result.evidence)}.
            </p>
            <div className="hero-actions">
              <Button small icon="eye" onClick={() => onOpen(result)}>
                Open public record
              </Button>
              <Button small icon="share">
                Copy link
              </Button>
            </div>
          </div>
        ) : null}
      </section>
      <Card title="Recent organization verification records">
        <div className="list">
          {rows.map(row => (
            <button
              type="button"
              className="list-row"
              key={String(row.id)}
              onClick={() => {
                setCode(String(row.name));
                setResult(row);
              }}
              style={{ width: "100%", textAlign: "left", color: "inherit" }}
            >
              <span className="list-icon">
                <Icon name="certificate" />
              </span>
              <span className="list-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.owner)} · {String(row.updated)}
                </span>
              </span>
              <Status>{String(row.status)}</Status>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FinanceLayout({
  spec,
  rows,
  onOpen,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="v2-stack">
      <div className="finance-summary">
        {spec.metrics.map(m => (
          <div className="finance-card" key={m.label}>
            <span>{m.label}</span>
            <strong>{m.value}</strong>
            <small>{m.trend}</small>
          </div>
        ))}
      </div>
      <div className="v2-grid">
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
        <div className="v2-stack">
          <Card title="Utilisation">
            <MiniChart values={spec.chart} />
          </Card>
          <Card title="Financial controls">
            <Notes spec={spec} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function IntegrationHealthLayout({
  spec,
  rows,
  onOpen,
  onTest,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onTest: () => void;
}) {
  return (
    <div className="v2-stack">
      <div className="v2-card-grid">
        {rows.map((row, i) => (
          <article className="entity-card-v2" key={String(row.id)}>
            <div className="entity-card-top">
              <span className="entity-card-icon">
                <Icon name={i % 2 ? "pulse" : "link"} />
              </span>
              <span className="entity-card-copy">
                <b>{String(row.name)}</b>
                <span>
                  {String(row.owner)} · last sync {String(row.updated)}
                </span>
              </span>
              <Status>{i === 5 ? "Attention" : "Healthy"}</Status>
            </div>
            <div className="entity-card-meta">
              <div>
                <span>Health</span>
                <b>{String(row.evidence)}</b>
              </div>
              <div>
                <span>Retries</span>
                <b>{i === 5 ? "12" : "0"}</b>
              </div>
            </div>
            <div className="entity-card-foot">
              <Button small icon="pulse" onClick={onTest}>
                Diagnose
              </Button>
              <Button small icon="eye" onClick={() => onOpen(row)}>
                Details
              </Button>
            </div>
          </article>
        ))}
      </div>
      <div className="v2-grid equal">
        <Card title="Latency and success trend">
          <MiniChart values={spec.chart} />
        </Card>
        <Card title="Operational ownership">
          <Notes spec={spec} />
        </Card>
      </div>
    </div>
  );
}

function ExportLayout({
  spec,
  rows,
  onOpen,
  onCreate,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onCreate: () => void;
}) {
  const [purpose, setPurpose] = useState("Governance evidence review");
  const [mask, setMask] = useState(true);
  return (
    <div className="v2-grid">
      <div className="v2-stack">
        <Card
          title="Request a purpose-limited export"
          subtitle="Exports require role, purpose, scope, masking, approval and expiry."
        >
          <div className="form-fields-v2">
            <div className="form-field-v2 full">
              <label>Purpose</label>
              <input value={purpose} onChange={e => setPurpose(e.target.value)} />
            </div>
            <div className="form-field-v2">
              <label>Dataset</label>
              <select>
                <option>Governance evidence</option>
                <option>Enrolment aggregate</option>
                <option>Workforce capability aggregate</option>
                <option>Integration audit</option>
              </select>
            </div>
            <div className="form-field-v2">
              <label>Expiry</label>
              <select>
                <option>24 hours</option>
                <option>7 days</option>
                <option>30 days</option>
              </select>
            </div>
          </div>
          <div className="toggle-row-v2">
            <span>Mask direct identifiers</span>
            <button
              type="button"
              className={`toggle-v2 ${mask ? "on" : ""}`}
              onClick={() => setMask(v => !v)}
            />
          </div>
          <div className="hero-actions">
            <Button icon="lock" variant="primary" onClick={onCreate}>
              Submit export request
            </Button>
          </div>
        </Card>
        <RecordsTable spec={spec} rows={rows} onOpen={onOpen} />
      </div>
      <Card title="Export control requirements">
        <Notes spec={spec} />
      </Card>
    </div>
  );
}

function LayoutRenderer({
  spec,
  rows,
  onOpen,
  onPrimary,
  onExport: _onExport,
  onMove,
  onAdvance,
  onDecision,
  onSaveForm,
  formValues,
  setFormValues,
  policyToggles,
  setPolicyToggles,
  onTest,
  onHandoff,
  onNavigate,
  canManage,
}: {
  spec: WorkspaceSpec;
  rows: WorkspaceRow[];
  onOpen: (row: WorkspaceRow) => void;
  onPrimary: () => void;
  onExport: () => void;
  onMove: (id: string, status: string) => void;
  onAdvance: (id: string) => void;
  onDecision: (id: string, status: string) => void;
  onSaveForm: () => void;
  formValues: Record<string, string>;
  setFormValues: (v: Record<string, string>) => void;
  policyToggles: Record<string, boolean>;
  setPolicyToggles: (v: Record<string, boolean>) => void;
  onTest: () => void;
  onHandoff: () => void;
  onNavigate: (area: string, page: string) => void;
  canManage: boolean;
}) {
  switch (spec.layout) {
    case "dashboard":
      return <DashboardLayout spec={spec} rows={rows} onOpen={onOpen} onNavigate={onNavigate} />;
    case "queue":
      return <QueueLayout spec={spec} rows={rows} onOpen={onOpen} onMove={onMove} />;
    case "scorecard":
      return <ScorecardLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "calendar":
      return <CalendarLayout spec={spec} rows={rows} onOpen={onOpen} onAdd={onPrimary} />;
    case "form":
      return (
        <FormLayout
          spec={spec}
          values={formValues}
          setValues={setFormValues}
          onSave={onSaveForm}
          onHistory={
            (spec.id === "settings-profile" || spec.id === "institution-profile") && rows[0]
              ? () => onOpen(rows[0])
              : undefined
          }
          canManage={canManage}
        />
      );
    case "hierarchy":
      return <HierarchyLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "directory":
      return <DirectoryLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "onboarding":
      return <OnboardingLayout spec={spec} rows={rows} onOpen={onOpen} onAdvance={onAdvance} />;
    case "matrix":
      return <MatrixLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "import":
      return <ImportLayout spec={spec} rows={rows} onOpen={onOpen} onRun={onPrimary} />;
    case "portfolio":
      return <PortfolioLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "map":
      return <MapLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "table":
      return <TableLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "timetable":
      return <TimetableLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "library":
      return <LibraryLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "connection":
      return (
        <ConnectionLayout spec={spec} rows={rows} onOpen={onOpen} onTest={onTest} onHandoff={onHandoff} />
      );
    case "sessions":
      return <SessionsLayout spec={spec} rows={rows} onOpen={onOpen} onHandoff={onHandoff} />;
    case "policy":
      return (
        <PolicyLayout
          spec={spec}
          rows={rows}
          toggles={policyToggles}
          setToggles={setPolicyToggles}
          onOpen={onOpen}
        />
      );
    case "capacity":
      return <CapacityLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "evidence":
      return <EvidenceLayout spec={spec} rows={rows} onOpen={onOpen} onDecision={onDecision} />;
    case "analytics":
      return <AnalyticsLayout spec={spec} onGenerate={onPrimary} />;
    case "board":
      return <BoardLayout spec={spec} rows={rows} onOpen={onOpen} onMove={onMove} />;
    case "rubric":
      return <RubricLayout spec={spec} rows={rows} onSave={onSaveForm} />;
    case "media":
      return <MediaLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "wellbeing":
      return <WellbeingLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "advising":
      return <AdvisingLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "aid":
      return <AidLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "partnership":
      return <PartnershipLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "publications":
      return <PublicationsLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "verification":
      return <VerificationLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "finance":
      return <FinanceLayout spec={spec} rows={rows} onOpen={onOpen} />;
    case "integration-health":
      return <IntegrationHealthLayout spec={spec} rows={rows} onOpen={onOpen} onTest={onTest} />;
    case "export":
      return <ExportLayout spec={spec} rows={rows} onOpen={onOpen} onCreate={onPrimary} />;
    default:
      throw new Error(`No distinct organization layout registered for ${spec.id}: ${spec.layout}`);
  }
}

function WorkspacePage({
  academic,
  commercial,
  spec,
  tenancy,
  role,
  records,
  setRecords,
  onToast,
  onModal,
  onNavigate,
  onSwitchRole,
  onDetail,
}: {
  spec: WorkspaceSpec;
  academic: AcademicDirectory;
  commercial: CommercialDirectory;
  tenancy: TenancyDirectory;
  role: RoleProfile;
  records: WorkspaceRow[];
  setRecords: (rows: WorkspaceRow[]) => void;
  onToast: (title: string, message: string, tone?: Toast["tone"]) => void;
  onModal: (modal: ModalState) => void;
  onNavigate: (area: string, page: string) => void;
  onSwitchRole: () => void;
  onDetail: (detail: DetailState) => void;
}) {
  const identityAccount = useIdentity();
  const identityPage = isIdentityDirectory(spec.id);
  const identityPermission = ["people-roles", "people-access", "roles-permissions"].includes(spec.id)
    ? "identity.role.grant"
    : "identity.invitation.create";
  const legacyCanView = spec.accessRoles
      ? spec.accessRoles.includes(role.id)
      : roleHas(role, "areas", spec.area),
    legacyCanManage = spec.manageRoles
      ? spec.manageRoles.includes(role.id)
      : roleHas(role, "manage", spec.area),
    canApprove =
      isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)
        ? false
        : spec.approveRoles
          ? spec.approveRoles.includes(role.id)
          : roleHas(role, "approve", spec.area);
  const canView =
    isFinanceDirectory(spec.id) ||
    isParticipationDirectory(spec.id) ||
    academic.enabled ||
    commercial.enabled ||
    tenancy.enabled ||
    identityPage ||
    spec.id === "settings-security"
      ? true
      : legacyCanView;
  // D03 controls use current backend permissions and the approved existing dialog fields.
  const canManage =
    isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)
      ? false
      : commercial.enabled
        ? !!identityAccount.session?.permissions.some(
            permission => permission === "*" || permission === "commercial.license.manage",
          )
        : academic.enabled
          ? isAcademicCohortDirectory(spec.id) &&
            !!identityAccount.session?.permissions.includes("catalogue.cohort.manage")
          : tenancy.enabled
            ? tenancy.canManage
            : identityPage
              ? !!identityAccount.session?.permissions.some(p => p === "*" || p === identityPermission)
              : legacyCanManage;
  const organizationLabAnalytics = useOrganizationLabAnalytics(spec.id === "labs-analytics" && canView);
  const [query, setQuery] = useState(""),
    [status, setStatus] = useState("All"),
    [tab, setTab] = useState(spec.tabs[0]);
  const [formValues, setFormValues] = useState<Record<string, string>>(() =>
    isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id) || tenancy.enabled || commercial.enabled
      ? {}
      : safeGet(`organization-form-${spec.id}`, {}),
  );
  const [policyToggles, setPolicyToggles] = useState<Record<string, boolean>>(() =>
    safeGet(`organization-policy-${spec.id}`, {}),
  );
  useEffect(() => {
    setQuery("");
    setStatus("All");
    setTab(spec.tabs[0]);
    if (
      !(isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)) &&
      !tenancy.enabled &&
      !commercial.enabled
    )
      setFormValues(safeGet(`organization-form-${spec.id}`, {}));
    setPolicyToggles(safeGet(`organization-policy-${spec.id}`, {}));
  }, [commercial.enabled, spec.id, spec.tabs, tenancy.enabled]);
  useEffect(() => {
    if (tenancy.enabled)
      setFormValues(tenancy.profile ? tenancyFormValues("organization", tenancy.profile) : {});
  }, [tenancy.context, tenancy.enabled, tenancy.profile]);
  const filtered = useMemo(
    () =>
      records.filter(
        row =>
          (status === "All" || String(row.status) === status) &&
          Object.values(row).some(v => String(v).toLowerCase().includes(query.toLowerCase())),
      ),
    [records, status, query],
  );
  const exportRows = () => {
    const cols = spec.columns.map(c => c.key);
    const csv = [
      cols.map(csvEscape).join(","),
      ...records.map(r => cols.map(c => csvEscape(r[c])).join(",")),
    ].join("\n");
    downloadFile(`${spec.id}.csv`, csv, "text/csv");
    onToast("Export downloaded", `${spec.title} was exported with ${records.length} records.`);
  };
  const primary = () => {
    if (!canManage) {
      onToast("Read-only role", `${role.name} cannot create or change ${spec.title}.`, "warning");
      return;
    }
    if (tenancy.profilePage) {
      void saveForm();
      return;
    }
    onModal({ kind: "create", payload: { spec } });
  };
  const move = (id: string, next: string) => {
    if (isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)) {
      onToast("Action unavailable", "This finance action requires an approved server command.", "warning");
      return;
    }
    if (commercial.enabled) {
      onToast(
        "Governed commercial action required",
        "Use an approved commercial command to change this record.",
        "warning",
      );
      return;
    }
    if (academic.enabled) {
      if (isAcademicOrganizationPortfolio(spec.id)) return;
      const record = records.find(row => String(row.id) === id);
      if (record) onModal({ kind: "create", payload: { spec, record } });
      return;
    }
    if (tenancy.enabled) {
      onToast(
        "Governed tenancy action required",
        "Use the entity membership or structure controls for this record.",
        "warning",
      );
      return;
    }
    if (isIdentityDirectory(spec.id)) {
      onToast(
        "Identity state is managed by the Academy",
        "Use the invitation or role-assignment dialog to change access.",
        "warning",
      );
      return;
    }
    if (!canManage) {
      onToast("Read-only role", "This role cannot change workflow state.", "warning");
      return;
    }
    setRecords(records.map(r => (String(r.id) === id ? { ...r, status: next, updated: "Just now" } : r)));
    onToast("Workflow updated", `${spec.title} record moved to ${next}.`);
  };
  const advance = (id: string) => move(id, "Ready");
  const decision = (id: string, next: string) => {
    if (!canApprove) {
      onToast("Approval authority required", `${role.name} cannot make this decision.`, "warning");
      return;
    }
    move(id, next);
  };
  const saveForm = async () => {
    if (!canManage) return;
    if (tenancy.enabled) {
      try {
        await tenancy.saveProfile(formValues, formValues["Approved change reason"] || "");
        onToast("Profile saved", "The current entity profile was updated.");
      } catch (error) {
        onToast("Unable to save profile", error instanceof Error ? error.message : "Try again.", "warning");
      }
      return;
    }
    safeSet(`organization-form-${spec.id}`, formValues);
    safeSet(`organization-policy-${spec.id}`, policyToggles);
    onToast(
      "Governed changes saved",
      `${spec.title} settings were stored locally; production uses the NestJS API and audit log.`,
    );
  };
  const test = () =>
    onToast(
      "Connection diagnostics passed",
      `${spec.title} returned healthy authentication, authorization, latency and schema checks.`,
    );
  const handoff = () => {
    const domain = spec.external?.domain || "academy.chatoner.com";
    onToast(
      "Governed handoff unavailable",
      `${domain} has no server-issued Organization authority contract; no context or user data was sent.`,
      "warning",
    );
  };
  if (!canView) return <AccessBoundary spec={spec} role={role} onSwitchRole={onSwitchRole} />;
  if (spec.id === "labs-analytics") {
    const analytics = organizationLabAnalytics.data;
    const completed = analytics?.values.completed;
    return (
      <div
        className="workspace-root"
        data-route-id={spec.id}
        data-layout={spec.layout}
        data-model={spec.model}
        data-signature={spec.signature}
        data-d16-analytics-state="connected"
      >
        <span className="route-signature">{spec.signature}</span>
        <PageHero
          kicker={spec.areaLabel}
          title={spec.title}
          description="Privacy-thresholded verified Lab completions for the current Organization workspace only."
        >
          <Button
            icon="refresh"
            onClick={() => void organizationLabAnalytics.refresh()}
            disabled={organizationLabAnalytics.loading}
          >
            Refresh
          </Button>
        </PageHero>
        {organizationLabAnalytics.error ? (
          <Card>
            <div className="empty" role="alert">
              Governed analytics unavailable: {organizationLabAnalytics.error}
            </div>
          </Card>
        ) : null}
        <div className="v2-grid">
          <Card
            title="Verified Lab completions"
            subtitle={
              organizationLabAnalytics.loading
                ? "Loading governed projection…"
                : (analytics?.availability ?? "Unavailable")
            }
          >
            {completed !== undefined ? (
              <div className="metric-card">
                <span>Current unrevoked completions</span>
                <strong>{completed}</strong>
                <small>D10 evidence through the verified D17 Labs callback</small>
              </div>
            ) : !organizationLabAnalytics.loading ? (
              <div className="empty">
                {analytics?.availability === "not_projected"
                  ? "No Organization-scoped projection has been published yet."
                  : "No publishable cell is available; a small count remains suppressed."}
              </div>
            ) : null}
          </Card>
          <Card title="Metric governance">
            {analytics ? (
              <div className="notes">
                <div>
                  <b>
                    {analytics.metricKey} v{analytics.definitionVersion}
                  </b>
                  <p>
                    Source {analytics.sourceDomain} · minimum cell {analytics.minimumCellCount} · suppressed
                    cells {analytics.suppressedCells}
                  </p>
                </div>
                <div>
                  <b>Bounded evidence only</b>
                  <p>
                    Adoption, utilization, cost, support needs and outcome relationships are unavailable. This
                    projection grants no transaction or export authority.
                  </p>
                </div>
              </div>
            ) : (
              <div className="empty">Metric definition unavailable.</div>
            )}
          </Card>
        </div>
      </div>
    );
  }
  if (spec.layout === "analytics")
    return (
      <div
        className="workspace-root"
        data-route-id={spec.id}
        data-layout={spec.layout}
        data-model={spec.model}
        data-signature={spec.signature}
        data-d16-analytics-state="unavailable"
      >
        <span className="route-signature">{spec.signature}</span>
        <PageHero
          kicker={spec.areaLabel}
          title={spec.title}
          description="This view is waiting for approved metric definitions, purpose-limited owner facts and freshness evidence."
        />
        <Card>
          <div className="empty">
            No sample metrics or exports are shown. Academy Analytics will enable this route after its source,
            scope, suppression and freshness contract is certified.
          </div>
        </Card>
      </div>
    );
  return (
    <div
      className="workspace-root"
      data-route-id={spec.id}
      data-layout={spec.layout}
      data-model={spec.model}
      data-signature={spec.signature}
    >
      <span className="route-signature">{spec.signature}</span>
      <WorkspaceHero spec={spec} canManage={canManage} onPrimary={primary} onExport={exportRows} />
      <div className={`permission-banner ${canManage ? "" : "readonly"}`}>
        <span className="icon-wrap">
          <Icon name={canManage ? "shield" : "eye"} size={16} />
        </span>
        <div>
          <b>
            {academic.enabled || commercial.enabled || tenancy.enabled || identityPage
              ? `${identityAccount.session?.roles.join(", ") || "Current account"} · ${canManage ? "Management available" : "Directory access"}`
              : canManage
                ? `${role.name} can manage this workspace`
                : `${role.name} is viewing this workspace read-only`}
          </b>
          <p>
            {academic.enabled || commercial.enabled || tenancy.enabled || identityPage ? (
              `Scope: ${tenancy.profile?.name || identityAccount.session?.workspace.name || "Current workspace"}. Each action is checked against your current backend permissions.`
            ) : (
              <>
                Scope: {role.scope}.{" "}
                {canApprove
                  ? "This role also has approval authority here."
                  : "Approval actions require a separately authorized role."}{" "}
                Backend enforcement remains authoritative.
              </>
            )}
          </p>
        </div>
        <span className="toolbar-spacer" />
        <span className="backend-chip">
          <i className="online-dot" />
          {commercial.enabled
            ? commercial.error
              ? "Unable to load commercial records"
              : commercial.loading
                ? "Loading commercial records"
                : "Current commercial records"
            : academic.enabled
              ? academic.error
                ? "Unable to load cohorts"
                : academic.loading
                  ? "Loading cohorts"
                  : "Current academic records"
              : tenancy.enabled
                ? tenancy.error
                  ? "Unable to load records"
                  : tenancy.loading
                    ? "Loading records"
                    : "Current entity records"
                : "NestJS · Kafka · Redis ready"}
        </span>
      </div>
      <div className="workspace-tabs">
        {spec.tabs.map(item => (
          <button
            type="button"
            className={`workspace-tab ${tab === item ? "active" : ""}`}
            key={item}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}
      </div>
      <WorkspaceTabPanel title={spec.title} activeTab={tab} recordCount={filtered.length} />
      {spec.id === "settings-preferences" && (
        <Card
          title="New Program announcements"
          subtitle="Personal communication choice for the signed-in Account"
        >
          <ProgramAnnouncementPreference />
        </Card>
      )}
      {spec.layout !== "form" && spec.layout !== "analytics" ? (
        <FilterToolbar
          query={query}
          setQuery={setQuery}
          status={status}
          setStatus={setStatus}
          spec={{ ...spec, rows: records }}
          onExport={exportRows}
          onSavedView={() =>
            onToast("Saved view created", `${query || "All"} / ${status} was saved for ${spec.title}.`)
          }
        />
      ) : null}
      <Metrics items={spec.metrics} />
      <LayoutRenderer
        spec={spec}
        rows={filtered}
        onOpen={row => onDetail({ routeId: spec.id, row })}
        onPrimary={primary}
        onExport={exportRows}
        onMove={move}
        onAdvance={advance}
        onDecision={decision}
        onSaveForm={saveForm}
        formValues={formValues}
        setFormValues={setFormValues}
        policyToggles={policyToggles}
        setPolicyToggles={setPolicyToggles}
        onTest={test}
        onHandoff={handoff}
        onNavigate={onNavigate}
        canManage={canManage}
      />
    </div>
  );
}

function DetailDrawer({
  detail,
  spec,
  role,
  onClose,
  onUpdate,
  onEdit,
  onToast,
}: {
  detail: DetailState;
  spec: WorkspaceSpec;
  role: RoleProfile;
  onClose: () => void;
  onUpdate: (row: WorkspaceRow) => void;
  onEdit: (spec: WorkspaceSpec, row: WorkspaceRow) => void;
  onToast: (title: string, message: string, tone?: Toast["tone"]) => void;
}) {
  const account = useIdentity();
  const tenancyHistory = useTenancyHistory(
    "organization",
    detail?.routeId || "",
    detail ? String(detail.row.id) : undefined,
  );
  const academicHistory = useAcademicHistory(
    detail?.routeId || "",
    detail ? String(detail.row.id) : undefined,
  );
  const commercialHistory = useCommercialHistory(
    "organization",
    detail?.routeId || "",
    detail ? String(detail.row.id) : undefined,
  );
  const history = academicHistory.enabled
    ? academicHistory
    : commercialHistory.enabled
      ? commercialHistory
      : tenancyHistory;
  if (!detail) return null;
  const row = detail.row;
  const previewCanManage = spec.manageRoles
      ? spec.manageRoles.includes(role.id)
      : roleHas(role, "manage", spec.area),
    previewCanApprove = spec.approveRoles
      ? spec.approveRoles.includes(role.id)
      : roleHas(role, "approve", spec.area);
  const canManage =
    isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)
      ? false
      : commercialHistory.enabled
        ? !!account.session?.permissions.some(
            permission => permission === "*" || permission === "commercial.license.manage",
          )
        : isAcademicOrganizationPortfolio(spec.id)
          ? false
          : academicHistory.enabled
            ? !!account.session?.permissions.includes("catalogue.cohort.manage") &&
              !["completed", "cancelled"].includes(String(row.status))
            : history.enabled
              ? !!account.session?.permissions.some(
                  permission =>
                    permission === `tenancy.organization.${row.personId ? "members" : "structure"}.manage` ||
                    (!row.personId && permission === "tenancy.organization.members.manage"),
                )
              : !isIdentityDirectory(spec.id) && previewCanManage;
  const canApprove =
    isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)
      ? false
      : !commercialHistory.enabled &&
        !isAcademicOrganizationPortfolio(spec.id) &&
        !history.enabled &&
        !isIdentityDirectory(spec.id) &&
        previewCanApprove;
  const change = (status: string) => {
    onUpdate({ ...row, status, updated: "Just now" });
    onToast("Record updated", `${String(row.name)} is now ${status}.`);
    onClose();
  };
  return (
    <aside className="detail-drawer-v2" aria-label={`${String(row.name)} details`}>
      <div className="drawer-head-v2">
        <span className="list-icon">
          <Icon name={spec.icon} />
        </span>
        <span className="head-copy">
          <b>{String(row.name)}</b>
          <span>
            {spec.title} · {String(row.id)}
          </span>
        </span>
        <Status>{String(row.status)}</Status>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Close details">
          <Icon name="close" />
        </button>
      </div>
      <div className="detail-body-v2">
        <div className="detail-fields">
          {Object.entries(row).map(([key, value]) => (
            <div className="detail-field" key={key}>
              <span>{key.replace(/-/g, " ")}</span>
              <b>{String(value)}</b>
            </div>
          ))}
        </div>
        <Card
          title="Authority and history"
          subtitle={
            isAcademicOrganizationPortfolio(spec.id)
              ? "Published academic references"
              : history.enabled
                ? "Recorded changes in this entity"
                : isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)
                  ? "Finance audit evidence"
                  : "Attributable demonstration timeline"
          }
          className=""
        >
          {isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id) ? (
            <p>Finance audit history is not included in this view.</p>
          ) : isAcademicOrganizationPortfolio(spec.id) ? (
            <p>
              Version {String(row.programVersionId)} · Curriculum {String(row.curriculumVersionId)}. Selection
              audit history is not provided by this query.
            </p>
          ) : history.enabled ? (
            <div className="timeline" aria-live="polite">
              {history.loading && <p>Loading recorded history…</p>}
              {history.error && <p role="alert">{history.error}</p>}
              {!history.loading && !history.error && !history.items.length && (
                <p>No recorded changes are available.</p>
              )}
              {history.items.map(item => (
                <div className="timeline-item" key={item.id}>
                  <b>{item.action}</b>
                  <p>{item.reason}</p>
                  <p>
                    Actor: {item.actorId}
                    {item.effectiveActorId ? ` · Effective actor: ${item.effectiveActorId}` : ""}
                  </p>
                  <p>
                    {item.fromStatus ? `${item.fromStatus} → ` : ""}
                    {item.toStatus} · Version {item.version}
                  </p>
                  <time>{new Date(item.createdAt).toLocaleString()}</time>
                </div>
              ))}
            </div>
          ) : (
            <div className="timeline">
              <div className="timeline-item">
                <b>Record opened</b>
                <p>
                  {role.name} · {role.scope}
                </p>
                <time>{currentTime()}</time>
              </div>
              <div className="timeline-item">
                <b>Last status change</b>
                <p>
                  {String(row.owner)} · {String(row.updated)}
                </p>
                <time>{String(row.status)}</time>
              </div>
              <div className="timeline-item">
                <b>Policy evaluated</b>
                <p>Role, scope, purpose, segregation of duties and data sensitivity</p>
                <time>Passed</time>
              </div>
            </div>
          )}
        </Card>
        <Card title="Connected context">
          <Notes spec={spec} />
        </Card>
      </div>
      <div className="detail-foot-v2">
        <Button
          icon="edit"
          disabled={!canManage}
          onClick={() => (history.enabled ? onEdit(spec, row) : change("Active"))}
        >
          Edit / activate
        </Button>
        <Button icon="check" variant="green" disabled={!canApprove} onClick={() => change("Approved")}>
          Approve
        </Button>
        <Button
          icon="alert"
          variant="orange"
          disabled={history.enabled || !canManage}
          onClick={() => change("Needs attention")}
        >
          Request changes
        </Button>
        <Button
          icon="history"
          onClick={() =>
            history.enabled
              ? history.refresh()
              : onToast(
                  "Audit history opened",
                  `${String(row.name)} audit history is available to authorized roles.`,
                )
          }
        >
          Audit
        </Button>
      </div>
    </aside>
  );
}

function ActionModal({
  modal,
  onClose,
  onCreate,
  onSaved,
  onToast,
  role: _role,
  onRoleChange: _onRoleChange,
}: {
  modal: ModalState;
  onClose: () => void;
  onCreate: (spec: WorkspaceSpec, row: WorkspaceRow) => void;
  onSaved: () => void;
  onToast: (title: string, message: string, tone?: Toast["tone"]) => void;
  role: RoleProfile;
  onRoleChange: (role: RoleProfile) => void;
}) {
  const account = useIdentity();
  const [tenancyBusy, setTenancyBusy] = useState(false);
  const close = () => {
    if (!tenancyBusy) onClose();
  };
  const assignedGrants =
    account.access?.grants.filter(grant => grant.workspaceId === account.session?.workspace.id) || [];
  const [name, setName] = useState(""),
    [owner, setOwner] = useState("Organization Operations"),
    [scope, setScope] = useState("Whole organization tenant"),
    [status, setStatus] = useState("Draft");
  if (!modal) return null;
  const spec: WorkspaceSpec | undefined = modal.payload?.spec;
  let title: string,
    subtitle: string,
    icon: string | null = null,
    body: React.ReactNode,
    foot: React.ReactNode;
  if (modal.kind === "create" && spec && isAcademicCohortDirectory(spec.id)) {
    title = modal.payload?.record ? `Manage ${String(modal.payload.record.name)}` : spec.primaryAction;
    subtitle = "Use the exact published academic versions, schedule and a recorded change reason.";
    body = (
      <AcademicCohortFields
        kind="organization"
        record={modal.payload?.record}
        action={modal.payload?.academicAction}
        formId="academic-cohort-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button type="submit" form="academic-cohort-form" className="btn btn-primary" disabled={tenancyBusy}>
          {tenancyBusy ? "Recording…" : modal.payload?.record ? "Save changes" : "Create cohort"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec && isCommercialDirectory("organization", spec.id)) {
    title = modal.payload?.record
      ? `Commercial action · ${String(modal.payload.record.name)}`
      : spec.primaryAction;
    subtitle = "Choose the exact governed commercial record, capacity, validity and attributable reason.";
    body = (
      <CommercialActionFields
        entityKind="organization"
        route={spec.id}
        record={modal.payload?.record}
        formId="commercial-portal-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button
          className="btn btn-primary"
          type="submit"
          form="commercial-portal-form"
          disabled={tenancyBusy}
        >
          {tenancyBusy ? "Recording…" : "Record commercial action"}
        </button>
      </>
    );
  } else if (
    modal.kind === "create" &&
    spec &&
    (isTenancyDirectory("organization", spec.id) || modal.payload?.record?.personId)
  ) {
    title = modal.payload?.record ? `Edit ${String(modal.payload.record.name)}` : spec.primaryAction;
    subtitle = "Choose the current scope and record a reason for this change.";
    body = (
      <TenancyActionFields
        kind="organization"
        route={spec.id}
        record={modal.payload?.record}
        formId="tenancy-portal-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button className="btn btn-primary" type="submit" form="tenancy-portal-form" disabled={tenancyBusy}>
          {tenancyBusy ? "Recording…" : modal.payload?.record ? "Save changes" : "Create record"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec && isIdentityDirectory(spec.id)) {
    const operation = ["people-roles", "people-access", "roles-permissions"].includes(spec.id)
      ? "role-grant"
      : "invitation";
    title = operation === "invitation" ? "Invite workspace member" : "Assign workspace role";
    subtitle = "Choose a recipient, authorized role and scope, reason and expiry.";
    body = <IdentityAssignmentFields operation={operation} onClose={onClose} />;
    foot = null;
  } else if (modal.kind === "create" && spec) {
    title = spec.primaryAction;
    subtitle = `Create a distinct ${spec.title.toLowerCase()} record. Production validation and authorization remain server-enforced.`;
    body = (
      <div className="form-fields-v2">
        <div className="form-field-v2 full">
          <label>Name / title</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={`New ${spec.title.toLowerCase()} item`}
          />
        </div>
        <div className="form-field-v2">
          <label>Owner</label>
          <select value={owner} onChange={e => setOwner(e.target.value)}>
            <option>Organization Operations</option>
            <option>Governance Office</option>
            <option>Learning Operations</option>
            <option>People & Culture</option>
            <option>Program Office</option>
            <option>Finance Office</option>
          </select>
        </div>
        <div className="form-field-v2">
          <label>Scope</label>
          <select value={scope} onChange={e => setScope(e.target.value)}>
            <option>Whole organization tenant</option>
            <option>Customer Experience division</option>
            <option>Technology & Data team</option>
            <option>October 2026 cohort</option>
          </select>
        </div>
        <div className="form-field-v2">
          <label>Initial status</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option>Draft</option>
            <option>Active</option>
            <option>Review due</option>
          </select>
        </div>
        <div className="form-field-v2">
          <label>Backend domain</label>
          <input readOnly value={`${API_BASE}/organization/${spec.area}/${spec.id}`} />
        </div>
      </div>
    );
    foot = (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          icon="plus"
          variant="primary"
          disabled={!name.trim() || isTenancyDirectory("organization", spec.id)}
          onClick={() => {
            onCreate(spec, {
              id: `${spec.id.toUpperCase().slice(0, 4)}-${Date.now().toString().slice(-5)}`,
              name: name.trim(),
              owner,
              scope,
              status,
              progress: 0,
              updated: "Just now",
              date: "Not scheduled",
              time: "—",
              people: "0",
              evidence: "0%",
              risk: "Low",
              category: spec.group,
              decision: "Pending",
              code: `NEW-${Date.now().toString().slice(-4)}`,
            });
            onClose();
          }}
        >
          Create record
        </Button>
      </>
    );
  } else if (modal.kind === "role") {
    title = "Assigned organization roles";
    subtitle = "Your active role grants and their assigned scopes.";
    icon = "users";
    body = (
      <div className="v2-stack">
        {assignedGrants.map(r => (
          <button
            type="button"
            className="role-option active"
            key={r.id}
            onClick={() => onToast(r.name, r.permissions.join(", ") || "No permission keys assigned", "info")}
          >
            <span className="role-option-icon">
              <Icon name="shield" />
            </span>
            <span>
              <b>{r.name}</b>
              <span>{r.scopeType ? `${r.scopeType} · ${r.scopeId}` : account.session?.workspace.name}</span>
            </span>
            <Icon name="check" />
          </button>
        ))}
      </div>
    );
    foot = <Button onClick={onClose}>Close</Button>;
  } else if (modal.kind === "organization") {
    title = "Switch organization";
    subtitle = "Membership and role are independently verified for every organization.";
    icon = "building";
    body = (
      <div className="v2-stack">
        {(
          account.access?.memberships.filter(m =>
            workspaceClients(
              (account.access?.grants || []).filter(g => g.workspaceId === m.workspaceId).map(g => g.roleKey),
            ).includes("organizations"),
          ) || []
        )
          .map(m => [m.workspaceId, m.name, m.type])
          .map(item => (
            <button
              type="button"
              className="action-card"
              key={item[0]}
              onClick={async () => {
                try {
                  await selectIdentityWorkspace(item[0]);
                  window.location.reload();
                } catch (error) {
                  onToast(
                    "Workspace not changed",
                    error instanceof Error ? error.message : "Unable to switch workspace",
                    "warning",
                  );
                }
              }}
            >
              <span className="thread-avatar">{item[1].slice(0, 2).toUpperCase()}</span>
              <span>
                <strong>{item[1]}</strong>
                <span>{item[2]}</span>
              </span>
              <Icon name="arrow" />
            </button>
          ))}
      </div>
    );
    foot = <Button onClick={onClose}>Close</Button>;
  } else if (modal.kind === "help") {
    title = "Organization Hub help";
    subtitle = "Route-specific guidance and backend integration references.";
    icon = "help";
    body = (
      <div className="policy-list">
        <div className="policy-item">
          <Icon name="grid" />
          <span>
            <strong>79 distinct operational destinations</strong>
            <span>Every route has a registered layout, data model, actions and backend domain.</span>
          </span>
        </div>
        <div className="policy-item">
          <Icon name="users" />
          <span>
            <strong>Ten organization roles</strong>
            <span>Role, delegated scope and approval authority remain separate.</span>
          </span>
        </div>
        <div className="policy-item">
          <Icon name="link" />
          <span>
            <strong>Connected Academy services</strong>
            <span>Class, Labs and Build Rooms retain independent authorization.</span>
          </span>
        </div>
        <div className="policy-item">
          <Icon name="database" />
          <span>
            <strong>NestJS backend ready</strong>
            <span>Configure __CHATONER_ACADEMY_API_BASE__ before production deployment.</span>
          </span>
        </div>
      </div>
    );
    foot = <Button onClick={onClose}>Close</Button>;
  } else {
    title = "Confirm governed action";
    subtitle = "Review the action, authority and audit reason before continuing.";
    icon = "shield";
    body = <Notes spec={spec || PAGE_SPECS["command-dashboard"]} />;
    foot = (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => {
            onToast("Action confirmed", "The governed action was recorded.");
            onClose();
          }}
        >
          Confirm
        </Button>
      </>
    );
  }
  return (
    <div
      className="modal-v2"
      role="dialog"
      aria-modal="true"
      onMouseDown={e => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="modal-card-v2">
        <div className="modal-head-v2">
          <span className="modal-icon">
            <Icon name={icon || "shield"} />
          </span>
          <span className="copy">
            <b>{title}</b>
            <span>{subtitle}</span>
          </span>
          <button type="button" className="icon-button" onClick={close} disabled={tenancyBusy}>
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body-v2">{body}</div>
        <div className="modal-foot-v2">{foot}</div>
      </div>
    </div>
  );
}

function App() {
  const identity = useSignedInIdentity();
  const hashParts = location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const hashArea = hashParts[0] === "organization" ? hashParts[1] : "";
  const hashPage = hashParts[0] === "organization" ? hashParts[2] : "";
  const resolvedHashPage = hashPage ? LEGACY_ROUTE_ALIASES[`${hashArea}/${hashPage}`] || hashPage : "";
  const initialArea = hashArea || safeGet("organization-v2-area", "command"),
    initialPage = resolvedHashPage || safeGet("organization-v2-page", "command-dashboard"),
    initialRoleId = safeGet("organization-v2-role", "owner");
  const [activeArea, setActiveArea] = useState<string>(
    AREAS.some(a => a.id === initialArea) ? initialArea : "command",
  );
  const [activePage, setActivePage] = useState<string>(
    PAGE_SPECS[initialPage] ? initialPage : "command-dashboard",
  );
  const [activeRole, setActiveRole] = useState<RoleProfile>(
    ORGANIZATION_ROLES.find(r => r.id === initialRoleId) || ORGANIZATION_ROLES[0],
  );
  const [recordsByRoute, setRecordsByRoute] = useState<Record<string, WorkspaceRow[]>>(() =>
    Object.fromEntries(
      Object.entries(safeGet<Record<string, WorkspaceRow[]>>("organization-v2-records", {})).filter(
        ([id]) =>
          !isParticipationDirectory(id) &&
          !isFinanceDirectory(id) &&
          !isTenancyDirectory("organization", id) &&
          !isAcademicDirectory(id) &&
          !isCommercialDirectory("organization", id),
      ),
    ),
  );
  const [mobileNav, setMobileNav] = useState(false),
    [searchOpen, setSearchOpen] = useState(false),
    [search, setSearch] = useState(""),
    [notificationsOpen, setNotificationsOpen] = useState(false),
    [languageOpen, setLanguageOpen] = useState(false),
    [profileOpen, setProfileOpen] = useState(false),
    [roleOpen, setRoleOpen] = useState(false);
  const [modal, setModal] = useState<ModalState>(null),
    [detail, setDetail] = useState<DetailState>(null),
    [toasts, setToasts] = useState<Toast[]>([]),
    [selectedNotification, setSelectedNotification] = useState<any | null>(null),
    [primaryLanguage, setPrimaryLanguage] = useState("English"),
    [secondaryLanguage, setSecondaryLanguage] = useState("Luganda");
  const toast = (title: string, message: string, tone: Toast["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts(v => [...v, { id, title, message, tone }].slice(-4));
    setTimeout(() => setToasts(v => v.filter(t => t.id !== id)), 3600);
  };
  const area = AREAS.find(a => a.id === activeArea) || AREAS[0];
  const route = area.routes.find(r => r.id === activePage) || area.routes[0];
  const directory = useIdentityDirectory(route.id);
  const tenancy = useTenancyDirectory("organization", route.id);
  const academic = useAcademicDirectory(route.id);
  const commercial = useCommercialDirectory("organization", route.id);
  const finance = useFinanceDirectory(route.id);
  const participation = useParticipation(
    ["people-learners", "people-members", "labs-entitlements", "finance-plan"].includes(route.id),
    true,
  );
  const sourceSpec = PAGE_SPECS[route.id];
  const originalSpec =
    sourceSpec && participation.data
      ? {
          ...sourceSpec,
          notes: sourceSpec.notes.map((note, index) =>
            index === 0 ? { ...note, body: participation.data!.notice } : note,
          ),
        }
      : sourceSpec;
  if (!originalSpec) throw new Error(`Missing PAGE_SPECS entry for ${route.id}`);
  const spec = isParticipationDirectory(route.id)
    ? {
        ...originalSpec,
        rows: [],
        chart: [],
        metrics: originalSpec.metrics.map(metric => ({
          ...metric,
          value: participation.data ? "0" : "—",
          trend:
            participation.error || (participation.loading ? "Loading" : "No canonical entity allocations"),
        })),
        notes: originalSpec.notes.map(note => ({
          ...note,
          body: participation.error || participation.data?.notice || "Loading participation records.",
        })),
      }
    : finance.enabled
      ? {
          ...originalSpec,
          rows: finance.rows,
          chart: [],
          metrics: originalSpec.metrics.map(metric => ({
            ...metric,
            value: "—",
            trend: finance.error || (finance.loading ? "Loading" : "Current scoped finance records"),
          })),
          notes: originalSpec.notes.map(note => ({ ...note, body: finance.error || finance.notice })),
        }
      : commercial.enabled
        ? {
            ...originalSpec,
            rows: commercial.rows,
            chart: [],
            metrics: originalSpec.metrics.map((metric, index) => ({
              ...metric,
              value:
                index === 0 && !commercial.loading && !commercial.error && !commercial.hasMore
                  ? String(commercial.rows.length)
                  : "—",
              trend:
                commercial.error ||
                (commercial.loading
                  ? "Loading"
                  : commercial.hasMore
                    ? "First 500 commercial records loaded"
                    : "Current commercial records"),
            })),
            notes: originalSpec.notes.map((note, index) =>
              index === 0
                ? {
                    ...note,
                    body:
                      commercial.error ||
                      (commercial.loading
                        ? "Loading current commercial licenses."
                        : "License capacity and validity come from the Academy commercial catalogue. Assigned or used seats remain owned by enrollment."),
                  }
                : note,
            ),
          }
        : academic.enabled
          ? {
              ...originalSpec,
              rows: academic.rows,
              chart: [],
              metrics: originalSpec.metrics.map(metric => ({
                ...metric,
                value:
                  academic.data && !academic.data.hasMore && metric.label === "Open cohorts"
                    ? String(
                        academic.data.items.filter(item => ["open", "limited"].includes(item.status)).length,
                      )
                    : "—",
                trend: academic.error || (academic.loading ? "Loading" : "Current scoped catalogue"),
              })),
              notes: originalSpec.notes.map(note => ({
                ...note,
                body: isAcademicOrganizationPortfolio(route.id)
                  ? "Stored Organization selections reference the current published Academy catalogue. Selection does not grant enrollment, learning access or commercial entitlement."
                  : "Cohort definitions and dates come from the selected Organization. Enrollment and learning progress are managed separately.",
              })),
            }
          : tenancy.enabled
            ? {
                ...originalSpec,
                rows: tenancy.rows,
                chart: [],
                metrics: originalSpec.metrics.map(metric => ({
                  ...metric,
                  value:
                    tenancy.profile && ["Teams", "Active entities", "Legal entities"].includes(metric.label)
                      ? String(
                          metric.label === "Legal entities"
                            ? 1
                            : tenancy.rows.filter(row =>
                                metric.label === "Teams" ? row.unitType === "team" : row.status === "active",
                              ).length,
                        )
                      : "—",
                  trend: tenancy.error || (tenancy.loading ? "Loading" : "Current entity records"),
                })),
                notes: originalSpec.notes.map((note, index) =>
                  index === 0
                    ? {
                        ...note,
                        body:
                          tenancy.error ||
                          (tenancy.loading
                            ? "Loading current entity records."
                            : tenancy.profile
                              ? `${tenancy.profile.name} · ${tenancy.profile.status}`
                              : "No entity context is available."),
                      }
                    : note,
                ),
              }
            : directory.enabled
              ? {
                  ...originalSpec,
                  rows: directory.rows,
                  metrics: originalSpec.metrics.map((metric, index) => ({
                    ...metric,
                    value: index === 0 && directory.data ? String(directory.rows.length) : "—",
                    trend:
                      directory.error ||
                      (directory.loading
                        ? "Loading"
                        : index === 0
                          ? directory.data?.hasMore
                            ? "First 500 records; refine before exporting"
                            : "Current workspace records"
                          : "Not reported"),
                  })),
                }
              : originalSpec;
  const records = isParticipationDirectory(route.id)
    ? []
    : finance.enabled
      ? finance.rows
      : commercial.enabled
        ? commercial.rows
        : academic.enabled
          ? academic.rows
          : tenancy.enabled
            ? tenancy.rows
            : directory.enabled
              ? directory.rows
              : recordsByRoute[spec.id] || spec.rows;
  const setRecords = (rows: WorkspaceRow[]) =>
    !(isFinanceDirectory(spec.id) || isParticipationDirectory(spec.id)) &&
    !commercial.enabled &&
    !isAcademicDirectory(spec.id) &&
    setRecordsByRoute(current => ({ ...current, [spec.id]: rows }));
  const navigate = useCallback((areaId: string, pageId?: string) => {
    const next = AREAS.find(a => a.id === areaId) || AREAS[0];
    const pid = pageId && next.routes.some(r => r.id === pageId) ? pageId : next.routes[0].id;
    setActiveArea(next.id);
    setActivePage(pid);
    history.replaceState(null, "", `#/organization/${next.id}/${pid}`);
    setMobileNav(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setSelectedNotification(null);
    setLanguageOpen(false);
    setProfileOpen(false);
    setRoleOpen(false);
    setDetail(null);
    window.scrollTo(0, 0);
  }, []);
  const coreInbox = useCoreNotifications();
  const notificationRecords = coreInbox.notifications.map(item => ({
    ...item,
    time: new Date(item.createdAt).toLocaleString(),
    unread: !item.readAt,
  }));
  const unread = coreInbox.unreadCount;
  useEffect(() => {
    safeSet("organization-v2-area", activeArea);
    safeSet("organization-v2-page", activePage);
    safeSet("organization-v2-role", activeRole.id);
  }, [activeArea, activePage, activeRole]);
  useEffect(() => {
    const onHash = () => {
      const parts = location.hash.replace(/^#\/?/, "").split("/").filter(Boolean);
      if (parts[0] !== "organization") return;
      const nextArea = parts[1],
        raw = parts[2];
      const next = AREAS.find(a => a.id === nextArea);
      if (!next) return;
      const resolved = LEGACY_ROUTE_ALIASES[`${nextArea}/${raw}`] || raw;
      if (next.routes.some(r => r.id === resolved)) {
        setActiveArea(nextArea);
        setActivePage(resolved);
      }
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    safeSet("organization-v2-records", recordsByRoute);
  }, [recordsByRoute]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        setMobileNav(false);
        setNotificationsOpen(false);
        setLanguageOpen(false);
        setProfileOpen(false);
        setRoleOpen(false);
        setModal(null);
        setDetail(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    (window as any).__CHATONER_ORGANIZATION_PLATFORM__ = {
      version: BUILD_VERSION,
      routeCount: Object.keys(PAGE_SPECS).length,
      layoutCount: new Set(Object.values(PAGE_SPECS).map(v => v.layout)).size,
      activeArea,
      activePage,
      activeRole: activeRole.id,
      apiBase: API_BASE,
    };
    (window as any).__CHATONER_ORGANIZATION_NAVIGATE__ = (area: string, page: string) => navigate(area, page);
  }, [activeArea, activePage, activeRole, navigate]);
  const grouped = useMemo(
    () =>
      area.routes.reduce<Record<string, RouteDef[]>>((acc, r) => {
        (acc[r.group] ||= []).push(r);
        return acc;
      }, {}),
    [area],
  );
  const searchResults = useMemo(() => {
    const q = search.toLowerCase().trim();
    const routeResults = AREAS.flatMap(a =>
      a.routes.map(r => ({
        kind: "route",
        area: a,
        route: r,
        label: r.title,
        detail: `${a.label} · ${r.description}`,
      })),
    ).filter(x => !q || `${x.label} ${x.detail}`.toLowerCase().includes(q));
    const recordResults = Object.values(PAGE_SPECS)
      .flatMap(s =>
        (isFinanceDirectory(s.id)
          ? []
          : isCommercialDirectory("organization", s.id)
            ? s.id === spec.id
              ? commercial.rows
              : []
            : isAcademicDirectory(s.id)
              ? s.id === spec.id
                ? academic.rows
                : []
              : isIdentityDirectory(s.id)
                ? s.id === spec.id
                  ? directory.rows
                  : []
                : recordsByRoute[s.id] || s.rows
        )
          .slice(0, 3)
          .map(row => ({
            kind: "record",
            area: AREAS.find(a => a.id === s.area)!,
            route: AREAS.find(a => a.id === s.area)!.routes.find(r => r.id === s.id)!,
            label: String(row.name),
            detail: `${s.title} · ${String(row.owner)} · ${String(row.status)}`,
          })),
      )
      .filter(x => q && `${x.label} ${x.detail}`.toLowerCase().includes(q));
    return [...routeResults, ...recordResults].slice(0, 40);
  }, [search, recordsByRoute, spec.id, directory.rows, academic.rows, commercial.rows]);
  const createRecord = (target: WorkspaceSpec, row: WorkspaceRow) => {
    if (isFinanceDirectory(target.id) || isCommercialDirectory("organization", target.id)) {
      toast("Not saved", "Use an approved commercial command to create this record.", "warning");
      return;
    }
    if (
      isAcademicDirectory(target.id) ||
      isIdentityDirectory(target.id) ||
      isTenancyDirectory("organization", target.id)
    ) {
      toast(
        "Not saved",
        "This dialog needs the recipient, role and scope controls before it can submit an identity request.",
        "warning",
      );
      return;
    }
    const current = recordsByRoute[target.id] || target.rows;
    setRecordsByRoute(v => ({ ...v, [target.id]: [row, ...current] }));
    toast("Record created", `${String(row.name)} was added to ${target.title}.`);
  };
  const updateDetail = (row: WorkspaceRow) => {
    const target = PAGE_SPECS[detail?.routeId || spec.id];
    if (isFinanceDirectory(target.id) || isCommercialDirectory("organization", target.id)) {
      toast("Not saved", "Use an approved commercial command to change this record.", "warning");
      return;
    }
    if (
      isAcademicDirectory(target.id) ||
      isIdentityDirectory(target.id) ||
      isTenancyDirectory("organization", target.id)
    ) {
      toast("Not saved", "Identity changes require the authorized identity action.", "warning");
      return;
    }
    const current = recordsByRoute[target.id] || target.rows;
    setRecordsByRoute(v => ({
      ...v,
      [target.id]: current.map(r => (String(r.id) === String(row.id) ? row : r)),
    }));
  };
  const switchRole = (role: RoleProfile) => {
    setActiveRole(role);
    safeSet("organization-v2-role", role.id);
  };
  const openDetail = (d: DetailState) => setDetail(d);
  const roleLabel = identity.role || "No assigned role";
  return (
    <div className="app-shell">
      <style>{STYLES + V2_STYLES + dropdownThemeStyles}</style>
      {mobileNav ? <div className="mobile-overlay" onClick={() => setMobileNav(false)} /> : null}
      <aside className={`rail ${mobileNav ? "mobile-open" : ""}`} aria-label="Organization areas">
        <button
          className="brand-mark"
          onClick={() => navigate("command", "command-dashboard")}
          aria-label="Organization Command"
          title="Organization Command"
        >
          <img
            className="academy-platform-mark"
            src={academyMark}
            alt=""
            width={837}
            height={765}
            decoding="async"
          />
          <span className="rail-tip">Organization Command</span>
        </button>
        <div className="rail-scroll">
          {AREAS.map(a => (
            <button
              key={a.id}
              className={`rail-button ${activeArea === a.id ? "active" : ""}`}
              onClick={() => navigate(a.id, a.routes[0].id)}
              aria-label={a.label}
              title={a.label}
            >
              <Icon name={a.icon} />
              {a.id === "command" ? <span className="rail-badge">12</span> : null}
              <span className="rail-tip">{a.label}</span>
            </button>
          ))}
        </div>
        <div className="rail-bottom">
          <button
            className="rail-button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            title="Search"
          >
            <Icon name="search" />
            <span className="rail-tip">Search</span>
          </button>
          <button
            className="rail-button"
            onClick={() => setModal({ kind: "help" })}
            aria-label="Help"
            title="Help"
          >
            <Icon name="help" />
            <span className="rail-tip">Help</span>
          </button>
        </div>
      </aside>
      <aside className={`sidebar ${mobileNav ? "mobile-open" : ""}`} aria-label={`${area.label} navigation`}>
        <div className="academy-platform-sidebar-brand">
          <img
            className="academy-platform-logo"
            src={academyLogo}
            alt="Chatoner AI Academy"
            width={4124}
            height={967}
            decoding="async"
          />
        </div>
        <div className="sidebar-head">
          <button className="organization-switch" onClick={() => setModal({ kind: "organization" })}>
            <span className="organization-avatar">{identity.initials}</span>
            <span className="organization-copy">
              <b>{directory.account.session?.workspace.name || "Academy workspace"}</b>
              <span>Organization Platform v{BUILD_VERSION}</span>
            </span>
            <Icon name="chevron" size={13} />
          </button>
        </div>
        <div className="sidebar-body">
          <div className="sidebar-title">
            <div>
              <h2>{area.label}</h2>
              <p>{area.routes.length} distinct operational destinations</p>
            </div>
            <button
              type="button"
              className="icon-button"
              aria-label="Close organization navigation"
              onClick={() => setMobileNav(false)}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
          {Object.entries(grouped).map(([group, routes]) => (
            <div className="nav-group" key={group}>
              <div className="nav-group-label">{group}</div>
              {routes.map(r => (
                <button
                  key={r.id}
                  className={`nav-link ${activePage === r.id ? "active" : ""}`}
                  onClick={() => navigate(area.id, r.id)}
                >
                  <Icon name={r.icon} size={15} />
                  <span className="nav-label">{r.title}</span>
                  {r.badge ? <span className="nav-badge">{r.badge}</span> : null}
                </button>
              ))}
            </div>
          ))}
          <div className="sidebar-card">
            <b>10 organization roles · 79 workspaces</b>
            <p>
              Every destination has its own operational model. Role, scope, purpose and approval are enforced
              by the backend.
            </p>
            <Button small icon="users" onClick={() => setModal({ kind: "role" })}>
              View assigned roles
            </Button>
          </div>
        </div>
      </aside>
      <main className="main-shell">
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            aria-label="Open navigation"
            onClick={() => setMobileNav(true)}
          >
            <Icon name="menu" />
          </button>
          <div className="breadcrumb">
            <div className="eyebrow">{area.label}</div>
            <h1>{route.title}</h1>
          </div>
          <div className="top-actions">
            <button className="search-trigger" onClick={() => setSearchOpen(true)}>
              <Icon name="search" size={15} />
              <span>Search all 79 workspaces…</span>
              <kbd>⌘K</kbd>
            </button>
            <div className="topbar-role">
              <button
                type="button"
                className="role-button"
                onClick={() => {
                  setRoleOpen(v => !v);
                  setLanguageOpen(false);
                  setNotificationsOpen(false);
                  setProfileOpen(false);
                }}
              >
                <span className="role-icon">
                  <Icon name="shield" size={14} />
                </span>
                <span className="role-button-copy">
                  <b>{roleLabel}</b>
                  <span>{activeRole.scope}</span>
                </span>
                <Icon name="chevron" size={12} />
              </button>
              {roleOpen ? (
                <div className="role-menu">
                  {ORGANIZATION_ROLES.map(r => (
                    <button
                      type="button"
                      className={`role-option ${activeRole.id === r.id ? "active" : ""}`}
                      key={r.id}
                      onClick={() => {
                        switchRole(r);
                        setRoleOpen(false);
                        toast("Role preview changed", `${r.name} is now active.`);
                      }}
                    >
                      <span className="role-option-icon">
                        <Icon name="shield" />
                      </span>
                      <span>
                        <b>{r.name}</b>
                        <span>{r.scope}</span>
                      </span>
                      {activeRole.id === r.id ? <Icon name="check" /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              className={`language-button ${languageOpen ? "active" : ""}`}
              onClick={() => {
                setLanguageOpen(v => !v);
                setRoleOpen(false);
                setProfileOpen(false);
                setNotificationsOpen(false);
              }}
            >
              <Icon name="globe" size={15} />
              <span>
                {LANGUAGES.find(l => l[0] === primaryLanguage)?.[1]} +{" "}
                {LANGUAGES.find(l => l[0] === secondaryLanguage)?.[1]}
              </span>
            </button>
            <button
              type="button"
              className={`icon-button ${notificationsOpen ? "active" : ""}`}
              aria-label="Open notifications"
              onClick={() => {
                setNotificationsOpen(v => !v);
                setLanguageOpen(false);
                setRoleOpen(false);
                setProfileOpen(false);
              }}
            >
              <Icon name="bell" size={16} />
              {unread ? <span className="dot" /> : null}
            </button>
            <button
              className="profile-button"
              onClick={() => {
                setProfileOpen(v => !v);
                setLanguageOpen(false);
                setRoleOpen(false);
                setNotificationsOpen(false);
              }}
            >
              <span className="profile-avatar">{identity.initials}</span>
              <span className="profile-copy">
                <b>{identity.name}</b>
                <span>{identity.role || "No assigned role"}</span>
              </span>
              <Icon name="chevron" size={12} />
            </button>
          </div>
        </header>
        <div className="content">
          <WorkspacePage
            key={spec.id}
            spec={spec}
            tenancy={tenancy}
            academic={academic}
            commercial={commercial}
            role={activeRole}
            records={records}
            setRecords={setRecords}
            onToast={toast}
            onModal={setModal}
            onNavigate={navigate}
            onSwitchRole={() => setModal({ kind: "role" })}
            onDetail={openDetail}
          />
        </div>
      </main>
      <nav className="mobile-bottom" aria-label="Mobile navigation">
        {[
          ["command", "home", "Command"],
          ["people", "users", "People"],
          ["learning", "book", "Learning"],
          ["class", "video", "Class"],
          ["labs", "flask", "Labs"],
        ].map(([id, icon, label]) => (
          <button
            key={id}
            className={`mobile-nav-button ${activeArea === id ? "active" : ""}`}
            onClick={() => navigate(id, AREAS.find(a => a.id === id)!.routes[0].id)}
          >
            <Icon name={icon} size={18} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      {searchOpen ? (
        <div
          className="overlay"
          onMouseDown={e => {
            if (e.target === e.currentTarget) setSearchOpen(false);
          }}
        >
          <div className="search-modal">
            <div className="search-input-row">
              <Icon name="search" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search routes, people, programs, records and decisions…"
              />
              <button className="icon-button" onClick={() => setSearchOpen(false)}>
                <Icon name="close" />
              </button>
            </div>
            <div className="search-results">
              {searchResults.length ? (
                searchResults.map((result: any, i) => (
                  <button
                    className="search-result"
                    key={`${result.route.id}-${result.label}-${i}`}
                    onClick={() => navigate(result.area.id, result.route.id)}
                  >
                    <span className="list-icon">
                      <Icon name={result.route.icon} />
                    </span>
                    <span className="search-result-copy">
                      <b>{result.label}</b>
                      <span>{result.detail}</span>
                    </span>
                    <Icon name="arrow" />
                  </button>
                ))
              ) : (
                <div className="empty">No matching organization records.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {notificationsOpen ? (
        <div className={`drawer notification-list-drawer ${selectedNotification ? "detail-open" : ""}`}>
          <div className="drawer-head">
            <h3>Organization notifications</h3>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                small
                onClick={() => {
                  void coreInbox
                    .markAllRead()
                    .then(() => toast("Notifications marked read", "Your notification history was updated."))
                    .catch(() => toast("Notifications unavailable", "Try again later.", "warning"));
                }}
              >
                Mark all read
              </Button>
              <button
                className="icon-button"
                onClick={() => {
                  setNotificationsOpen(false);
                  setSelectedNotification(null);
                }}
              >
                <Icon name="close" />
              </button>
            </div>
          </div>
          <div className="drawer-body">
            {selectedNotification ? (
              <div className="notification-detail-view">
                <button className="notification-back" onClick={() => setSelectedNotification(null)}>
                  <Icon name="arrow" size={14} /> Back to notifications
                </button>
                <span className="notification-detail-icon">
                  <Icon name="bell" />
                </span>
                <span className="notification-detail-label">Read · {selectedNotification.time}</span>
                <h4>{selectedNotification.title}</h4>
                <p>{selectedNotification.body}</p>
                <Button
                  variant="primary"
                  onClick={() => {
                    const access = new URL(platformEnvironment.appOrigins.auth);
                    const prefix = access.pathname.replace(/\/auth\/?$/, "");
                    if (selectedNotification.deepLink?.startsWith("/"))
                      window.location.assign(access.origin + prefix + selectedNotification.deepLink);
                  }}
                >
                  Open related page
                </Button>
              </div>
            ) : (
              notificationRecords.map((n: any) => (
                <button
                  key={n.id}
                  className={`notification ${n.unread ? "unread" : ""}`}
                  style={{ width: "100%", textAlign: "left", color: "inherit" }}
                  onClick={() => {
                    if (n.unread)
                      void coreInbox
                        .markRead(String(n.id))
                        .catch(() => toast("Notifications unavailable", "Try again later.", "warning"));
                    setSelectedNotification(n);
                  }}
                >
                  <span className="notification-dot" />
                  <span className="notification-copy">
                    <b>{n.title}</b>
                    <p>{n.body}</p>
                    <span>{n.time}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
      {languageOpen ? (
        <div className="dropdown" style={{ right: 112 }}>
          <div className="nav-group-label" style={{ paddingTop: 5 }}>
            Primary language
          </div>
          {LANGUAGES.slice(0, 8).map(([name, code]) => (
            <button
              key={name}
              className={`dropdown-item ${primaryLanguage === name ? "active" : ""}`}
              onClick={() => {
                setPrimaryLanguage(name);
                setLanguageOpen(false);
                toast("Primary language updated", `${name} is now the primary interface language.`);
              }}
            >
              <Icon name="globe" size={14} />
              <span style={{ flex: 1 }}>{name}</span>
              <b>{code}</b>
            </button>
          ))}
          <div className="nav-group-label" style={{ paddingTop: 9 }}>
            Secondary translation
          </div>
          <select
            className="select"
            value={secondaryLanguage}
            onChange={e => setSecondaryLanguage(e.target.value)}
          >
            {LANGUAGES.filter(l => l[0] !== primaryLanguage).map(([name]) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </div>
      ) : null}
      {profileOpen ? (
        <div className="dropdown">
          <button
            className="dropdown-item"
            onClick={() => {
              navigate("settings", "settings-profile");
              setProfileOpen(false);
            }}
          >
            <Icon name="user" />
            <span>Organization profile settings</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              setModal({ kind: "role" });
              setProfileOpen(false);
            }}
          >
            <Icon name="eye" />
            <span>View assigned roles</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              setModal({ kind: "organization" });
              setProfileOpen(false);
            }}
          >
            <Icon name="building" />
            <span>Switch organization</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              setModal({ kind: "help" });
              setProfileOpen(false);
            }}
          >
            <Icon name="help" />
            <span>Help and route guide</span>
          </button>
          <button className="dropdown-item" onClick={signOut}>
            <Icon name="logout" />
            <span>Sign out</span>
          </button>
        </div>
      ) : null}
      <DetailDrawer
        detail={detail}
        spec={detail ? PAGE_SPECS[detail.routeId] : spec}
        role={activeRole}
        onClose={() => setDetail(null)}
        onUpdate={updateDetail}
        onEdit={(editSpec, row) => setModal({ kind: "create", payload: { spec: editSpec, record: row } })}
        onToast={toast}
      />
      <ActionModal
        modal={modal}
        onClose={() => setModal(null)}
        onCreate={createRecord}
        onSaved={() => {
          setModal(null);
          setDetail(null);
          toast("Change recorded", "The directory has been refreshed from the backend.");
        }}
        onToast={toast}
        role={activeRole}
        onRoleChange={switchRole}
      />
      <div className="toast-stack" aria-live="polite">
        {toasts.map(t => (
          <div className="toast" key={t.id}>
            <span className="toast-icon">
              <Icon name={t.tone === "warning" ? "alert" : "check"} size={15} />
            </span>
            <div>
              <b>{t.title}</b>
              <p>{t.message}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
